import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isLoggedIn } from '../lib/authState';
import { algeriaDateTimeToInstant, algeriaNowHHMM, algeriaTodayYMD, addMinutesToHHMM } from '../lib/algeriaTime';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, MapPin, User, X as XIcon, Settings, Users, Save,
  GraduationCap, Globe, Shield, Phone, DollarSign, FileText, Plus,
  TrendingUp, CheckCircle, AlertCircle, Star, CreditCard, AlertTriangle,
  ArrowRightLeft, Bell, ChevronRight, Trash2, Activity, Printer, MessageSquare, Send, Inbox, RefreshCw as RefreshIcon, Search as SearchIcon, Video,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ImageUploader from '../components/ImageUploader';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { format, parseISO, isBefore, startOfDay, startOfMonth, isAfter } from 'date-fns';
import { fr, ar } from 'date-fns/locale';
import QRCode from 'qrcode';
import { computeDuplicateGroups, computeSameSubstanceGroups } from '../utils/medicationDuplicates';
import { PRE_VISIT_ALLERGY_OPTIONS, PRE_VISIT_MEDICATION_OPTIONS, PRE_VISIT_CONTEXT_OPTIONS, preVisitTagLabels } from '../data/preVisitOptions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Securite : echappement HTML pour toute valeur patient/medecin injectee
// dans la fenetre d'impression construite par concatenation de chaines
// (document.write). Sans cela, un champ libre (nom, diagnostic, note...)
// contenant du HTML/JS est execute dans le contexte de l'appli (XSS stockee).
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const TIME_SLOTS = [
  '08:00','08:30','09:00','09:30','10:00','10:30',
  '11:00','11:30','12:00','12:30',
  // Correctif (demande Nadjib, 20/09/2026) : "12:30" sautait directement a
  // "14:00", comme si tous les medecins prenaient systematiquement une
  // pause dejeuner a cette heure precise. Le backend n'impose aucune limite
  // d'horaire (voir _validate_schedule, routes/doctors.py) — ces deux
  // creneaux etaient simplement absents de la liste, donc impossibles a
  // cocher meme pour un medecin qui ne fait pas de pause a ce moment-la.
  // Chaque medecin reste libre de les laisser decoches pour definir SA
  // propre pause (horaire et duree), en ne cochant que les creneaux ou il
  // consulte reellement — comme pour n'importe quel autre creneau de cette
  // liste.
  '13:00','13:30',
  '14:00','14:30',
  '15:00','15:30','16:00','16:30','17:00','17:30',
  // ✅ Elargi (demande Nadjib, 08/09/2026) pour permettre de tester ce soir
  // avec le compte medecin test, et plus generalement pour les medecins qui
  // consultent le soir — le backend n'a jamais impose de limite d'horaire
  // (voir _validate_schedule, routes/doctors.py), seule cette liste cote
  // frontend bloquait les creneaux apres 17h30.
  '18:00','18:30','19:00','19:30','20:00','20:30',
  '21:00','21:30','22:00'
];

// Correctif (demande Nadjib, 20/09/2026) : un creneau de planning etait
// jusqu'ici une simple chaine "HH:MM" - "propose la teleconsultation" etait
// un interrupteur global par medecin, applicable a N'IMPORTE LEQUEL de ses
// creneaux, meme ceux ou il n'est pas physiquement au cabinet (ex: un
// creneau 20h ou le medecin ne fait QUE de la video). Un creneau peut
// desormais etre soit l'ancien format (chaine, retro-compatibilite - tous
// les creneaux jamais retouches depuis ce correctif), soit un objet
// {time, types} qui precise explicitement s'il propose "in_person",
// "video", ou les deux. Memes fonctions cote serveur, voir
// slot_time/slot_consultation_types (backend/utils.py) - la regle ne doit
// jamais diverger entre affichage et sauvegarde.
const slotEntryTime = (entry) => (typeof entry === 'string' ? entry : entry?.time);
const slotEntryTypes = (entry, videoConsultationEnabled) => {
  if (typeof entry === 'string') {
    return videoConsultationEnabled ? ['in_person', 'video'] : ['in_person'];
  }
  return entry?.types && entry.types.length ? entry.types : ['in_person'];
};

const LANGUAGE_OPTIONS = ['Français', 'Arabe', 'Anglais', 'Tamazight', 'Espagnol'];
const INSURANCE_OPTIONS = ['CNAS', 'CASNOS', 'Mutuelle', 'MGen', 'Saidal'];
const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const SPECIALTIES_MAP = {
  general: 'Medecine generale', cardiology: 'Cardiologie', dermatology: 'Dermatologie',
  gynecology: 'Gynecologie', ophthalmology: 'Ophtalmologie', pediatrics: 'Pediatrie',
  dentistry: 'Dentiste', orthopedics: 'Orthopedie', psychiatry: 'Psychiatrie',
  neurology: 'Neurologie', urology: 'Urologie', ent: 'ORL', radiology: 'Radiologie',
  gastroenterology: 'Gastro-enterologie', pneumology: 'Pneumologie',
  general_surgery: 'Chirurgie generale', endocrinology: 'Endocrinologie',
  anesthesiology: 'Anesthesiologie', rheumatology: 'Rhumatologie',
  physical_medicine: 'Medecine physique', nephrology: 'Nephrologie',
  neurosurgery: 'Neurochirurgie',
};

// ====== SUIVI DE MESURES (tension, poids, acuite visuelle, etc.) ======
// Phase 1 du catalogue enrichi : toutes les mesures sont desormais reunies
// dans UN SEUL catalogue, organise par grande categorie clinique, et
// accessible a TOUS les medecins (avant, une mesure comme la saturation en
// oxygene n'etait proposee qu'aux pneumologues par exemple). Les quelques
// mesures les plus utilisees pour la specialite du medecin connecte
// remontent en haut du menu ("Suggere pour votre specialite"), le reste du
// catalogue complet reste juste en dessous, range par categorie. Le medecin
// garde aussi la possibilite de saisir un type "Autre..." personnalise.
const MEASUREMENT_CATALOG = {
  vitals: {
    label: 'Constantes vitales',
    items: [
      { value: 'tension_arterielle', label: 'Tension artérielle', unit: 'mmHg', placeholder: '120/80' },
      { value: 'frequence_cardiaque', label: 'Fréquence cardiaque', unit: 'bpm', placeholder: '72' },
      { value: 'temperature', label: 'Température', unit: '°C', placeholder: '37' },
    ],
  },
  respiratory: {
    label: 'Respiratoire',
    items: [
      { value: 'saturation_o2', label: 'Saturation en oxygène', unit: '%', placeholder: '98' },
      { value: 'frequence_respiratoire', label: 'Fréquence respiratoire', unit: '/min', placeholder: '16' },
      { value: 'dep', label: 'Débit expiratoire de pointe (DEP)', unit: 'L/min', placeholder: '450' },
    ],
  },
  anthropometry: {
    label: 'Anthropométrie',
    items: [
      { value: 'poids', label: 'Poids', unit: 'kg', placeholder: '70' },
      { value: 'taille', label: 'Taille', unit: 'cm', placeholder: '175' },
      { value: 'tour_taille', label: 'Tour de taille', unit: 'cm', placeholder: '90' },
      { value: 'perimetre_cranien', label: 'Périmètre crânien', unit: 'cm', placeholder: '35' },
      { value: 'imc', label: 'IMC', unit: 'kg/m²', placeholder: '22' },
    ],
  },
  metabolic: {
    label: 'Métabolique / Biologie',
    items: [
      { value: 'glycemie', label: 'Glycémie', unit: 'g/L', placeholder: '1.00' },
      { value: 'crp', label: 'CRP', unit: 'mg/L', placeholder: '5' },
      { value: 'psa', label: 'PSA', unit: 'ng/mL', placeholder: '1.5' },
    ],
  },
  ophthalmology: {
    label: 'Ophtalmologie',
    items: [
      { value: 'acuite_visuelle_od', label: 'Acuité visuelle (œil droit)', unit: '/10', placeholder: '10' },
      { value: 'acuite_visuelle_og', label: 'Acuité visuelle (œil gauche)', unit: '/10', placeholder: '10' },
      { value: 'tension_oculaire', label: 'Tension oculaire', unit: 'mmHg', placeholder: '16' },
    ],
  },
  pain: {
    label: 'Douleur / Symptômes',
    items: [
      { value: 'echelle_douleur', label: 'Échelle de douleur (EVA)', unit: '/10', placeholder: '4' },
    ],
  },
  other: {
    label: 'Autres mesures spécialisées',
    items: [
      { value: 'diametre_lesion', label: 'Diamètre de lésion', unit: 'mm', placeholder: '5' },
      { value: 'nombre_lesions', label: 'Nombre de lésions', unit: '', placeholder: '3' },
      { value: 'profondeur_poche', label: 'Profondeur de poche parodontale', unit: 'mm', placeholder: '3' },
      { value: 'dents_traitees', label: 'Dents traitées', unit: '', placeholder: '2' },
      { value: 'amplitude_articulaire', label: 'Amplitude articulaire', unit: '°', placeholder: '90' },
      { value: 'score_evaluation', label: "Score d'évaluation", unit: 'points', placeholder: '12' },
      { value: 'force_musculaire', label: 'Force musculaire', unit: '/5', placeholder: '4' },
      { value: 'audiometrie', label: 'Audiométrie', unit: 'dB', placeholder: '20' },
      { value: 'glasgow', label: 'Score de Glasgow', unit: '/15', placeholder: '15' },
    ],
  },
};
// Index a plat (type -> definition) construit une seule fois a partir du
// catalogue ci-dessus, pour les lookups rapides (label, unite, placeholder).
const MEASUREMENT_INDEX = Object.values(MEASUREMENT_CATALOG).reduce((acc, cat) => {
  cat.items.forEach((item) => { acc[item.value] = item; });
  return acc;
}, {});
// Suggestions par specialite : seulement les 2-3 mesures les plus utilisees
// pour cette pratique, mises en avant en haut du menu. Le catalogue complet
// reste toujours accessible juste en dessous, par categorie — plus aucune
// mesure n'est "reservee" a une specialite.
const SPECIALTY_SUGGESTIONS = {
  cardiology: ['tension_arterielle', 'frequence_cardiaque'],
  pediatrics: ['poids', 'taille', 'perimetre_cranien'],
  ophthalmology: ['acuite_visuelle_od', 'acuite_visuelle_og', 'tension_oculaire'],
  endocrinology: ['glycemie', 'poids'],
  nephrology: ['tension_arterielle', 'poids'],
  pneumology: ['saturation_o2', 'frequence_respiratoire'],
  gynecology: ['poids', 'tension_arterielle'],
  general: ['tension_arterielle', 'poids', 'temperature'],
  dermatology: ['diametre_lesion', 'nombre_lesions'],
  dentistry: ['profondeur_poche', 'dents_traitees'],
  orthopedics: ['amplitude_articulaire', 'echelle_douleur'],
  psychiatry: ['poids', 'score_evaluation'],
  neurology: ['tension_arterielle', 'force_musculaire'],
  urology: ['psa', 'poids'],
  ent: ['audiometrie', 'saturation_o2'],
  gastroenterology: ['poids'],
  general_surgery: ['tension_arterielle', 'temperature'],
  anesthesiology: ['tension_arterielle', 'saturation_o2'],
  rheumatology: ['echelle_douleur', 'crp'],
  physical_medicine: ['amplitude_articulaire', 'echelle_douleur'],
  neurosurgery: ['glasgow', 'tension_arterielle'],
};
const getMeasurementLabel = (type) => {
  const def = MEASUREMENT_INDEX[type];
  return def ? def.label : type;
};

// Icone + couleur par type de mesure, pour un affichage regroupe plus lisible
// qu'une simple liste plate. Fallback deterministe (hash du type) pour les
// types personnalises saisis via "Autre..." qui ne sont pas dans la table.
const MEASUREMENT_TYPE_META = {
  poids: { icon: '⚖️', color: '#0596DE' },
  taille: { icon: '📏', color: '#8B5CF6' },
  temperature: { icon: '🌡️', color: '#F59E0B' },
  frequence_cardiaque: { icon: '❤️', color: '#EF4444' },
  tension_arterielle: { icon: '🩺', color: '#DC2626' },
  perimetre_cranien: { icon: '👶', color: '#0EA5E9' },
  acuite_visuelle_od: { icon: '👁️', color: '#6366F1' },
  acuite_visuelle_og: { icon: '👁️', color: '#6366F1' },
  tension_oculaire: { icon: '👁️', color: '#6366F1' },
  glycemie: { icon: '🩸', color: '#DB2777' },
  saturation_o2: { icon: '🫁', color: '#0D9488' },
  frequence_respiratoire: { icon: '🫁', color: '#0D9488' },
  imc: { icon: '⚖️', color: '#0596DE' },
  diametre_lesion: { icon: '🔬', color: '#7C3AED' },
  nombre_lesions: { icon: '🔬', color: '#7C3AED' },
  profondeur_poche: { icon: '🦷', color: '#0891B2' },
  dents_traitees: { icon: '🦷', color: '#0891B2' },
  amplitude_articulaire: { icon: '🦴', color: '#CA8A04' },
  echelle_douleur: { icon: '🎚️', color: '#EA580C' },
  score_evaluation: { icon: '📋', color: '#4B5563' },
  force_musculaire: { icon: '💪', color: '#B45309' },
  psa: { icon: '🩸', color: '#DB2777' },
  audiometrie: { icon: '👂', color: '#0891B2' },
  crp: { icon: '🩸', color: '#DB2777' },
  glasgow: { icon: '🧠', color: '#7C3AED' },
  tour_taille: { icon: '📐', color: '#0EA5E9' },
  dep: { icon: '💨', color: '#0D9488' },
};
const DEFAULT_MEASUREMENT_COLORS = ['#0596DE', '#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#0EA5E9', '#DB2777', '#CA8A04'];
const hashMeasurementType = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0; }
  return Math.abs(h);
};
const getMeasurementTypeMeta = (type) => {
  if (MEASUREMENT_TYPE_META[type]) return MEASUREMENT_TYPE_META[type];
  return { icon: '📊', color: DEFAULT_MEASUREMENT_COLORS[hashMeasurementType(type || '') % DEFAULT_MEASUREMENT_COLORS.length] };
};

// Phase 3 : IMC calcule automatiquement a partir du dernier poids et de la
// derniere taille du patient. N'est jamais enregistre en base — uniquement
// affiche, recalcule a chaque chargement du dossier.
const getAutoBmi = (measurements) => {
  const list = measurements || [];
  const latestOfType = (t) => {
    const entries = list.filter((m) => m.type === t).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return entries[0] || null;
  };
  const latestPoids = latestOfType('poids');
  const latestTaille = latestOfType('taille');
  if (!latestPoids || !latestTaille) return null;
  const poidsKg = parseFloat(String(latestPoids.value).replace(',', '.'));
  const tailleCm = parseFloat(String(latestTaille.value).replace(',', '.'));
  if (!Number.isFinite(poidsKg) || !Number.isFinite(tailleCm) || tailleCm <= 0) return null;
  const bmi = poidsKg / ((tailleCm / 100) ** 2);
  if (!Number.isFinite(bmi) || bmi <= 0) return null;
  return { bmi, latestPoids, latestTaille };
};

// Petite courbe d'evolution en SVG pur (pas de nouvelle dependance) pour les
// mesures avec au moins 2 valeurs numeriques.
const MeasurementSparkline = ({ values, color }) => {
  if (!values || values.length < 2) return null;
  const w = 100, h = 28, pad = 4;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const coords = values.map((v, i) => {
    const x = pad + (i * (w - pad * 2)) / (values.length - 1);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const points = coords.map(([x, y]) => `${x},${y}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill={color} />
      ))}
    </svg>
  );
};

// ====== COMPOSANT MÉTRIQUE COMPACTE ======
const CompactMetricCard = ({ icon: Icon, label, value, color, subtext }) => (
  <div className="bg-white rounded-lg border border-[#E2E8F0] p-3 hover:shadow-md transition-all duration-200">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[#64748B] truncate">{label}</p>
        <p className="text-base font-bold text-[#0F172A] truncate">{value}</p>
        {subtext && <p className="text-xs text-[#0596DE] mt-0.5">{subtext}</p>}
      </div>
    </div>
  </div>
);

// ====== CLOCHE NOTIFICATIONS ======
const NotificationBell = ({ notifications, unreadCount, onMarkAllRead, onMarkRead }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl bg-white border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
      >
        <Bell className="w-5 h-5 text-[#475569]" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#EF4444] text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl border border-[#E2E8F0] shadow-xl z-50">
          <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
            <h3 className="font-semibold text-[#0F172A]">{t('doc_notifications_title')}</h3>
            {unreadCount > 0 && (
              <button onClick={onMarkAllRead} className="text-xs text-[#0596DE] hover:underline">
                {t('doc_mark_all_read')}
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-[#64748B] text-sm">{t('doc_no_notification')}</div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  onClick={() => onMarkRead(notif.notification_id)}
                  className={`p-4 border-b border-[#F1F5F9] cursor-pointer hover:bg-[#F8FAFC] transition-colors ${!notif.read ? 'bg-[#E6F4FB]' : ''}`}
                >
                  <p className="font-medium text-sm text-[#0F172A]">{notif.title}</p>
                  <p className="text-xs text-[#64748B] mt-1">{notif.message}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    {format(parseISO(notif.created_at), 'dd MMM à HH:mm', { locale: fr })}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ====== BANNIÈRE ABONNEMENT ======
// ✅ NOUVEAU (teleconsultation, paiement manuel BaridiMob/CCP) : validation
// cote client du format CCP/BaridiMob (retour immediat a la saisie) - meme
// regle que cote serveur (routes/doctors.py, update_doctor_profile), jamais
// substituee a elle : le serveur revalide de toute facon, ce controle cote
// client evite juste un aller-retour reseau pour une erreur de saisie
// evidente. 6 a 12 chiffres, espaces/tirets tolerees comme separateurs
// (ex. "0021054786 89"), chaine vide autorisee (champ pas encore rempli).
const isValidCcp = (value) => {
  const digitsOnly = (value || '').replace(/[\s-]/g, '');
  return digitsOnly === '' || /^\d{6,12}$/.test(digitsOnly);
};

// ✅ NOUVEAU (teleconsultation, paiement manuel BaridiMob/CCP) : version
// "acces rapide" du switch video + numero CCP, directement sur le tableau
// de bord principal - demande a l'usage (Nadjib, 07/09/2026), pour eviter
// au medecin de devoir aller dans l'onglet "Mon profil" puis cliquer
// "Modifier" juste pour ces deux reglages. Sauvegarde immediate (pas de
// mode edition), independante du formulaire profileForm de l'onglet Profil.
const TeleconsultationQuickSettings = ({ profile, onSaved }) => {
  const { t } = useLanguage();
  const [enabled, setEnabled] = useState(profile.video_consultation_enabled || false);
  const [ccp, setCcp] = useState(profile.video_payment_ccp || '');
  const [saving, setSaving] = useState(false);

  // Reste synchronise si le profil est modifie ailleurs (ex: onglet "Mon
  // profil") et rechargé via fetchData() cote parent.
  useEffect(() => {
    setEnabled(profile.video_consultation_enabled || false);
    setCcp(profile.video_payment_ccp || '');
  }, [profile.video_consultation_enabled, profile.video_payment_ccp]);

  const save = async (patch) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/doctor/profile`, patch, { withCredentials: true, headers: {} });
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (checked) => {
    setEnabled(checked);
    save({ video_consultation_enabled: checked });
  };

  return (
    <div className="mb-6 p-4 bg-white rounded-xl border border-[#E2E8F0]">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Video className="w-5 h-5 text-[#0596DE] flex-shrink-0" />
          <div>
            <p className="font-semibold text-[#0F172A]">{t('doc_video_consultation_label')}</p>
            <p className="text-sm text-[#64748B]">{t('doc_video_consultation_desc')}</p>
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={handleToggle} disabled={saving} data-testid="quick-video-consultation-switch" />
      </div>
      {enabled && (
        <div className="mt-4 flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs mb-1 block">{t('doc_video_payment_ccp_label')}</Label>
            <Input value={ccp} onChange={(e) => setCcp(e.target.value)} placeholder={t('doc_video_payment_ccp_placeholder')} className="rounded-xl" data-testid="quick-video-payment-ccp" />
          </div>
          <Button
            onClick={() => {
              if (!isValidCcp(ccp)) {
                toast.error(t('doc_video_payment_ccp_invalid'));
                return;
              }
              save({ video_payment_ccp: ccp });
            }}
            disabled={saving}
            className="bg-[#0596DE] hover:bg-[#0480C4] text-white"
            data-testid="quick-video-payment-ccp-save"
          >
            {saving ? t('doc_saving') : t('doc_save')}
          </Button>
        </div>
      )}
    </div>
  );
};

const SubscriptionBanner = ({ subscription, onPayClick }) => {
  const { t } = useLanguage();
  if (!subscription) return null;
  const { status, is_active, days_left } = subscription;
  if (status === 'trial' && is_active) {
    const isUrgent = days_left <= 7;
    return (
      <div className={`mb-6 p-4 rounded-xl border flex items-center justify-between gap-4 ${isUrgent ? 'bg-[#FEF3C7] border-[#F59E0B]' : 'bg-[#E6F4FB] border-[#0596DE]'}`}>
        <div className="flex items-center gap-3">
          <AlertCircle className={`w-5 h-5 flex-shrink-0 ${isUrgent ? 'text-[#F59E0B]' : 'text-[#0596DE]'}`} />
          <div>
            <p className={`font-semibold ${isUrgent ? 'text-[#92400E]' : 'text-[#0F172A]'}`}>
              {isUrgent ? `${t('doc_trial_urgent_prefix')} ${days_left} ${days_left > 1 ? t('doc_days_word') : t('doc_day_word')} !` : `${t('doc_trial_active_prefix')} ${days_left} ${t('doc_days_word')} ${t('doc_remaining_days_word')}`}
            </p>
            <p className="text-sm text-[#64748B]">{t('doc_trial_price_notice')}</p>
          </div>
        </div>
        {isUrgent && (
          <Button onClick={onPayClick} className="bg-[#F59E0B] hover:bg-[#D97706] text-white flex-shrink-0">
            <CreditCard className="w-4 h-4 mr-2" />{t('doc_subscribe_btn')}
          </Button>
        )}
      </div>
    );
  }
  if (status === 'active' && is_active) {
    return (
      <div className="mb-6 p-4 rounded-xl border bg-[#D1FAE5] border-[#10B981] flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
        <p className="font-semibold text-[#065F46]">{`${t('doc_sub_active_prefix')} ${days_left} ${t('doc_days_word')} ${t('doc_remaining_days_word')}`}</p>
      </div>
    );
  }
  return (
    <div className="mb-6 p-4 rounded-xl border bg-[#FEE2E2] border-[#EF4444] flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0" />
        <div>
          <p className="font-semibold text-[#7F1D1D]">{t('doc_sub_expired_title')}</p>
          <p className="text-sm text-[#64748B]">{t('doc_sub_expired_subtitle')}</p>
        </div>
      </div>
      <Button onClick={onPayClick} className="bg-[#EF4444] hover:bg-[#DC2626] text-white flex-shrink-0">
        <CreditCard className="w-4 h-4 mr-2" />{t('doc_renew_btn')}
      </Button>
    </div>
  );
};

// ====== RENDEZ-VOUS ======
const EnhancedAppointmentRow = ({ appointment, profile, dateFnsLocale, t, onEdit, onReschedule, onJoinVideo, videoJoinLoading, consultationDuration = 30 }) => {
  const patient = appointment.patient;
  const appointmentDate = parseISO(appointment.date);
  try {
    // Correctif fuseau horaire (signale par Nadjib, 08/09/2026) : date+heure
    // du RDV TOUJOURS en heure d'Algerie — `parse(...)` interpretait ca dans
    // le fuseau du navigateur du visiteur, ce qui faussait "deja passe /
    // trop tot / trop tard pour rejoindre" pour quiconque ne navigue pas
    // depuis l'Algerie (diaspora, voyage...). Voir lib/algeriaTime.js :
    // `startTime` sert desormais uniquement aux COMPARAISONS (isBefore,
    // etc.) ; les libelles affiches (endTimeStr, heure d'ouverture) sont
    // calcules a part, en pure arithmetique sur "HH:mm", jamais en
    // reformattant un de ces objets Date (qui redonnerait l'heure locale du
    // navigateur, pas celle d'Algerie).
    const startTime = algeriaDateTimeToInstant(appointment.date, appointment.time);
    const endTime = new Date(startTime.getTime() + consultationDuration * 60 * 1000);
    const endTimeStr = addMinutesToHHMM(appointment.time, consultationDuration);
    // Correctif : un RDV dont l'heure est deja passee ne doit plus pouvoir
    // etre "deplace" — le filtre du dessus ne regarde que le jour, pas
    // l'heure, donc un RDV du matin reste "a venir" toute la journee.
    const isAlreadyPast = isBefore(startTime, new Date());
    // ✅ NOUVEAU (teleconsultation) : le bouton "Rejoindre" ne doit etre
    // cliquable qu'a partir de 3 minutes avant l'heure du RDV, jamais des
    // jours a l'avance (feedback apres premier test en prod) — et jusqu'a
    // la fin normale de la consultation, pas au-dela. Meme regle cote
    // serveur (voir routes/appointments.py, get_video_token) : ceci n'est
    // qu'un confort d'affichage, pas la seule protection.
    const videoJoinWindowStart = new Date(startTime.getTime() - 3 * 60 * 1000);
    const videoJoinWindowStartLabel = addMinutesToHHMM(appointment.time, -3);
    const videoJoinReady = appointment.consultation_type === 'video' && !isBefore(new Date(), videoJoinWindowStart) && isBefore(new Date(), endTime);
    const videoJoinTooEarly = appointment.consultation_type === 'video' && isBefore(new Date(), videoJoinWindowStart);
    return (
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#F8FAFC] to-white rounded-xl border-l-4 border-[#0596DE] hover:shadow-md transition-all duration-200">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-full bg-[#E6F4FB] flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 text-[#0596DE]" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-[#0F172A] truncate">{patient?.name || '-'}</h4>
            <p className="text-xs text-[#64748B] truncate">{patient?.phone}</p>
            {/* Correctif visibilite : jusqu'ici, rien n'indiquait qu'un RDV
                etait une teleconsultation en dehors de la fenetre "Rejoindre"
                (3 min avant jusqu'a la fin) — passe ce delai, un RDV video et
                un RDV au cabinet etaient visuellement identiques dans cette
                liste. Ce tag reste affiche en permanence, independamment de
                l'heure. */}
            {appointment.consultation_type === 'video' && (
              <p className="text-xs text-[#0596DE] mt-1 flex items-center gap-1"><Video className="w-3 h-3 flex-shrink-0" />{t('doc_video_consultation_tag')}</p>
            )}
            {/* ✅ NOUVEAU (inspire de la salle d'attente virtuelle Doctolib) :
                des que le patient a ouvert le lien de la consultation video
                (champ arrived_at pose automatiquement, voir get_video_token),
                on l'affiche clairement au medecin plutot que de tenter de
                deviner automatiquement si la consultation a "vraiment" eu
                lieu — c'est au medecin de decider, avec cette info sous les
                yeux, s'il rejoint ou s'il marque le RDV non honore. */}
            {appointment.consultation_type === 'video' && appointment.status === 'confirmed' && appointment.arrived_at && (
              <p className="text-xs text-[#10B981] mt-1 flex items-center gap-1 font-medium">🟢 {t('doc_patient_connected_tag')}</p>
            )}
            {appointment.reason && <p className="text-xs text-[#0596DE] mt-1 truncate">📋 {appointment.reason}</p>}
            {/* Correctif (idee CGM/ClickDoc) : informations avant consultation
                renseignees par le patient (allergies cochees + "autre",
                traitements en cours) — visibles ici avant meme le debut de
                la consultation. Les allergies sont en rouge pour qu'elles
                sautent aux yeux du medecin. */}
            {(() => {
              const info = appointment.pre_visit_info;
              if (!info) return null;
              const allergyLabels = [
                ...preVisitTagLabels(info.allergies_tags, PRE_VISIT_ALLERGY_OPTIONS, t),
                ...(info.allergies_other ? [info.allergies_other] : []),
              ];
              const medicationLabels = [
                ...preVisitTagLabels(info.medications_tags, PRE_VISIT_MEDICATION_OPTIONS, t),
                ...(info.medications_other ? [info.medications_other] : []),
              ];
              const contextLabels = [
                ...preVisitTagLabels(info.visit_context_tags, PRE_VISIT_CONTEXT_OPTIONS, t),
                ...(info.additional_notes ? [info.additional_notes] : []),
              ];
              return (
                <>
                  {allergyLabels.length > 0 && (
                    <p className="text-xs text-[#EF4444] mt-1 truncate">⚠️ {t('doc_pre_visit_allergies')} : {allergyLabels.join(', ')}</p>
                  )}
                  {medicationLabels.length > 0 && (
                    <p className="text-xs text-[#64748B] mt-1 truncate">💊 {t('doc_pre_visit_medications')} : {medicationLabels.join(', ')}</p>
                  )}
                  {contextLabels.length > 0 && (
                    <p className="text-xs text-[#64748B] mt-1 truncate">📝 {contextLabels.join(', ')}</p>
                  )}
                </>
              );
            })()}
            {appointment.rescheduled_by === 'doctor' && <p className="text-xs text-[#F59E0B] mt-1">{t('doc_rescheduled_by_doctor')}</p>}
            {/* ✅ NOUVEAU (teleconsultation, paiement manuel BaridiMob/CCP) :
                reference que le patient a saisie a la reservation - sans
                cet affichage, le medecin n'avait aucun moyen de la retrouver
                pour verifier le virement avant de confirmer le RDV (le champ
                existait deja cote patient/API mais n'etait affiche nulle
                part cote medecin). */}
            {appointment.consultation_type === 'video' && appointment.payment_reference && (
              <p className="text-xs text-[#0596DE] mt-1">💳 {t('doc_payment_reference_label')} : {appointment.payment_reference}</p>
            )}
          </div>
        </div>
        <div className="text-center min-w-[130px] px-4">
          <div className="font-medium text-[#0F172A] text-sm">{format(appointmentDate, 'dd MMM', { locale: dateFnsLocale })}</div>
          <div className="text-xs text-[#64748B]">{appointment.time} - {endTimeStr}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Correctif L-DOCDASH-STATUT-FANTOME : l'ancienne heuristique
              affichait "Terminé" des l'instant ou l'heure du RDV etait
              depassee (isAlreadyPast), meme pour un RDV toujours "confirmed"
              ou "pending" en base — un medecin qui venait de confirmer un
              RDV pour un creneau tres proche le voyait donc immediatement
              afficher "Terminé", alors que rien n'avait ete cloture. Le vrai
              statut "no_show" est deja pose automatiquement par le backend
              (no_show.py, 2h apres l'heure du RDV si aucune presence n'a ete
              signalee) et "completed" n'est mis qu'a l'action explicite du
              medecin : on s'appuie donc uniquement sur le statut reel, comme
              deja fait plus bas dans ce meme fichier (section "Historique
              RDV"), plutot que sur une heuristique de date cote client. */}
          {/* ✅ NOUVEAU (teleconsultation, paiement manuel BaridiMob/CCP) : un
              RDV video "pending" attend une verification de virement, pas
              juste une confirmation de disponibilite comme un RDV
              presentiel - badge distinct pour que le medecin ne confirme
              pas par reflexe sans avoir verifie le paiement (voir
              payment_expiry.py pour l'expiration automatique du creneau). */}
          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${appointment.status === 'no_show' ? 'bg-[#F1F5F9] text-[#475569]' : appointment.status === 'completed' ? 'bg-[#F1F5F9] text-[#475569]' : appointment.status === 'confirmed' ? 'bg-[#D1FAE5] text-[#10B981]' : appointment.status === 'pending' ? 'bg-[#FEF3C7] text-[#F59E0B]' : 'bg-[#F1F5F9] text-[#475569]'}`}>
            {appointment.status === 'no_show' ? t('doc_no_show_badge') : appointment.status === 'completed' ? t('doc_completed') : appointment.status === 'confirmed' ? t('doc_confirmed') : appointment.status === 'pending' ? (appointment.consultation_type === 'video' ? t('doc_payment_pending_badge') : t('doc_pending_count')) : appointment.status}
          </span>
          {/* ✅ NOUVEAU (teleconsultation) : bouton visible uniquement pour un
              RDV video pas encore passe — le jeton (voir handleJoinVideo)
              n'est cree qu'au clic, jamais stocke a l'avance. Grise (pas
              cache) avant l'ouverture du creneau (3 min avant le RDV), avec
              l'heure d'ouverture affichee, plutot que de disparaitre sans
              explication. */}
          {videoJoinReady && (
            <Button size="sm" onClick={() => onJoinVideo(appointment)} disabled={videoJoinLoading} className="text-xs bg-[#0596DE] hover:bg-[#0480C4] text-white">
              <Video className="w-3 h-3 mr-1" />{t('doc_join_video_btn')}
            </Button>
          )}
          {videoJoinTooEarly && (
            <Button size="sm" disabled className="text-xs bg-[#F1F5F9] text-[#94A3B8] cursor-not-allowed">
              <Video className="w-3 h-3 mr-1" />{t('doc_join_video_too_early').replace('{time}', videoJoinWindowStartLabel)}
            </Button>
          )}
          {!isAlreadyPast && (
            <Button variant="outline" size="sm" onClick={() => onReschedule(appointment)} className="text-xs text-[#F59E0B] border-[#F59E0B] hover:bg-[#FEF3C7]">
              <ArrowRightLeft className="w-3 h-3 mr-1" />{t('doc_reschedule_btn')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onEdit(appointment)} className="text-xs">{t('doc_edit')}</Button>
        </div>
      </div>
    );
  } catch (error) {
    return (
      <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-xl">
        <div className="flex items-center gap-4 flex-1">
          <User className="w-6 h-6 text-[#0596DE]" />
          <div>
            <h4 className="font-medium text-[#0F172A]">{patient?.name || '-'}</h4>
            <p className="text-sm text-[#64748B]">{patient?.phone}</p>
          </div>
        </div>
        <div className="text-center">
          <div className="font-medium text-[#0F172A]">{format(appointmentDate, 'dd MMM', { locale: dateFnsLocale })}</div>
          <div className="text-sm text-[#64748B]">{appointment.time}</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => onEdit(appointment)}>{t('doc_edit')}</Button>
      </div>
    );
  }
};

// ====== GRAPHIQUE REVENUS ======
const RevenueChart = ({ appointments, profile, dateFnsLocale }) => {
  const { t } = useLanguage();
  const last7Days = Array.from({ length: 7 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() - (6 - i)); return d; });
  const revenueByDay = last7Days.map((date) => {
    const dayApts = appointments.filter((apt) => format(parseISO(apt.date), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd') && apt.status === 'completed');
    return dayApts.length * (profile.fees || 0);
  });
  const maxRevenue = Math.max(...revenueByDay, 1);
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
      <h3 className="text-lg font-semibold text-[#0F172A] mb-6">{t('doc_revenue_7d_title')}</h3>
      <div className="flex items-end justify-between gap-2" style={{ height: '200px' }}>
        {revenueByDay.map((revenue, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full bg-gradient-to-t from-[#0596DE] to-[#03A9F4] rounded-t-lg transition-all duration-300 hover:shadow-lg" style={{ height: `${Math.max((revenue / maxRevenue) * 100, 5)}%` }} title={`${revenue.toLocaleString()} DZD`} />
            <span className="text-xs text-[#64748B]">{format(last7Days[i], 'EEE', { locale: dateFnsLocale })}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="bg-[#F8FAFC] p-4 rounded-lg"><p className="text-xs text-[#64748B]">{t('doc_revenue_total_7d')}</p><p className="text-lg font-bold text-[#0596DE]">{revenueByDay.reduce((a, b) => a + b, 0).toLocaleString()} DZD</p></div>
        <div className="bg-[#F8FAFC] p-4 rounded-lg"><p className="text-xs text-[#64748B]">{t('doc_revenue_avg_day')}</p><p className="text-lg font-bold text-[#0596DE]">{Math.round(revenueByDay.reduce((a, b) => a + b, 0) / 7).toLocaleString()} DZD</p></div>
        <div className="bg-[#F8FAFC] p-4 rounded-lg"><p className="text-xs text-[#64748B]">{t('doc_revenue_best_day')}</p><p className="text-lg font-bold text-[#10B981]">{Math.max(...revenueByDay).toLocaleString()} DZD</p></div>
      </div>
    </div>
  );
};

// ====== STATS CONSULTATION ======
const ConsultationStats = ({ appointments, profile, t }) => {
  const monthStart = startOfMonth(new Date());
  const monthApts = appointments.filter((apt) => isAfter(parseISO(apt.date), monthStart));
  const confirmed = monthApts.filter((a) => a.status === 'confirmed').length;
  const completed = monthApts.filter((a) => a.status === 'completed').length;
  const cancelled = monthApts.filter((a) => a.status === 'cancelled').length;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-4">{t('doc_this_month')}</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center p-3 bg-[#D1FAE5] rounded-lg"><span className="font-medium text-[#10B981]">{t('doc_stats_completed')}</span><span className="text-xl font-bold text-[#10B981]">{completed}</span></div>
          <div className="flex justify-between items-center p-3 bg-[#FEF3C7] rounded-lg"><span className="font-medium text-[#F59E0B]">{t('doc_stats_confirmed')}</span><span className="text-xl font-bold text-[#F59E0B]">{confirmed}</span></div>
          <div className="flex justify-between items-center p-3 bg-[#FEE2E2] rounded-lg"><span className="font-medium text-[#EF4444]">{t('doc_stats_cancelled')}</span><span className="text-xl font-bold text-[#EF4444]">{cancelled}</span></div>
        </div>
      </div>
      <div className="bg-gradient-to-br from-[#0596DE] to-[#03A9F4] rounded-2xl p-6 text-white">
        <h3 className="text-lg font-semibold mb-4">{t('doc_revenue_label')}</h3>
        <div className="space-y-4">
          <div className="bg-white bg-opacity-20 p-4 rounded-lg">
            <p className="text-sm text-blue-100">{t('doc_total_this_month')}</p>
            <p className="text-3xl font-bold">{(completed * (profile.fees || 0)).toLocaleString()} <span className="text-lg">DZD</span></p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white bg-opacity-10 p-3 rounded-lg"><p className="text-xs text-blue-100">{t('doc_per_consultation')}</p><p className="text-lg font-bold">{(profile.fees || 0).toLocaleString()} DZD</p></div>
            <div className="bg-white bg-opacity-10 p-3 rounded-lg"><p className="text-xs text-blue-100">{t('doc_completion_rate')}</p><p className="text-lg font-bold">{monthApts.length > 0 ? Math.round((completed / monthApts.length) * 100) : 0}%</p></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ====== ONGLET ABONNEMENT ======
const SubscriptionTab = ({ subscription, onPayClick }) => {
  const { t } = useLanguage();
  if (!subscription) return (<div className="text-center py-12 text-[#64748B]"><CreditCard className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" /><p>{t('doc_sub_info_unavailable')}</p></div>);
  const { status, is_active, days_left, trial_end, paid_until, monthly_price } = subscription;
  const statusConfig = { trial: { label: t('doc_sub_status_trial'), color: '#0596DE', bg: '#E6F4FB' }, active: { label: t('doc_sub_status_active'), color: '#10B981', bg: '#D1FAE5' }, expired: { label: t('doc_sub_status_expired'), color: '#EF4444', bg: '#FEE2E2' } };
  const config = statusConfig[status] || statusConfig.trial;
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-6">{t('doc_my_subscription')}</h3>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: config.bg }}><CreditCard className="w-8 h-8" style={{ color: config.color }} /></div>
          <div>
            <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: config.bg, color: config.color }}>{config.label}</span>
            {days_left !== null && <p className="text-2xl font-bold text-[#0F172A] mt-1">{days_left} {days_left > 1 ? t('doc_days_word') : t('doc_day_word')} {days_left > 1 ? t('doc_remaining_days_word') : t('doc_remaining_word')}</p>}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {status === 'trial' && trial_end && <div className="p-4 bg-[#F8FAFC] rounded-xl"><p className="text-xs text-[#64748B]">{t('doc_trial_end_label')}</p><p className="font-semibold text-[#0F172A]">{new Date(trial_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>}
          {status === 'active' && paid_until && <div className="p-4 bg-[#F8FAFC] rounded-xl"><p className="text-xs text-[#64748B]">{t('doc_subscribed_until_label')}</p><p className="font-semibold text-[#0F172A]">{new Date(paid_until).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>}
          <div className="p-4 bg-[#F8FAFC] rounded-xl"><p className="text-xs text-[#64748B]">{t('doc_monthly_price_label')}</p><p className="font-semibold text-[#0F172A]">{(monthly_price || 2500).toLocaleString()} {t('doc_da_per_month')}</p></div>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-1">{t('doc_choose_payment_method')}</h3>
        <p className="text-sm text-[#64748B] mb-6">{t('doc_price_no_commitment')}</p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="border-2 border-[#E2E8F0] hover:border-[#0596DE] rounded-2xl p-5 flex flex-col items-center gap-3 transition-all duration-200 group">
            <div className="w-14 h-14 rounded-xl bg-[#E6F4FB] flex items-center justify-center group-hover:bg-[#0596DE] transition-colors"><CreditCard className="w-7 h-7 text-[#0596DE] group-hover:text-white" /></div>
            <div className="text-center"><p className="font-bold text-[#0F172A]">{t('doc_card_cib')}</p><p className="text-xs text-[#64748B] mt-1">{t('doc_secure_online_payment')}</p><p className="text-xs text-[#64748B]">{t('doc_via_chargily')}</p></div>
            <Button className="w-full bg-[#0596DE] hover:bg-[#047CBA] text-white" onClick={onPayClick}>{t('doc_pay_by_cib')}</Button>
          </div>
          <div className="border-2 border-[#E2E8F0] hover:border-[#F59E0B] rounded-2xl p-5 flex flex-col items-center gap-3 transition-all duration-200 group">
            <div className="w-14 h-14 rounded-xl bg-[#FEF3C7] flex items-center justify-center group-hover:bg-[#F59E0B] transition-colors"><Phone className="w-7 h-7 text-[#F59E0B] group-hover:text-white" /></div>
            <div className="text-center"><p className="font-bold text-[#0F172A]">BaridiMob</p><p className="text-xs text-[#64748B] mt-1">{t('doc_payment_via_app')}</p><p className="text-xs text-[#64748B]">Algérie Poste</p></div>
            <Button className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white" onClick={onPayClick}>{t('doc_pay_by_baridimob')}</Button>
          </div>
          <div className="border-2 border-[#E2E8F0] hover:border-[#10B981] rounded-2xl p-5 flex flex-col items-center gap-3 transition-all duration-200 group">
            <div className="w-14 h-14 rounded-xl bg-[#D1FAE5] flex items-center justify-center"><FileText className="w-7 h-7 text-[#10B981]" /></div>
            <div className="text-center"><p className="font-bold text-[#0F172A]">{t('doc_bank_transfer')}</p><p className="text-xs text-[#64748B] mt-1">{`CPA — ${t('doc_activation_24h')}`}</p></div>
            <div className="w-full text-xs text-[#475569] bg-[#F8FAFC] rounded-xl p-3 space-y-1.5">
              <p><span className="font-semibold">{t('doc_bank_label')}</span> CPA</p>
              <p><span className="font-semibold">{t('doc_holder_label')}</span> TABBIBAK DZ</p>
              <p><span className="font-semibold">{t('doc_rib_label')}</span> 00799999000123456789 12</p>
              <p><span className="font-semibold">{t('doc_amount_label')}</span> 2 500,00 DA</p>
              <p><span className="font-semibold">{t('doc_reason_label')}</span> {t('doc_motif_value')}</p>
            </div>
            <p className="text-xs text-[#64748B] text-center">{t('doc_send_receipt_to')} <strong>contact@tabbibak.dz</strong></p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-4">{t('doc_whats_included')}</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {['doc_incl_online_booking','doc_incl_full_dashboard','doc_incl_appointment_mgmt','doc_incl_stats_revenue','doc_incl_visible_profile','doc_incl_priority_support'].map((key, i) => <p key={i} className="text-sm text-[#475569]">{t(key)}</p>)}
        </div>
      </div>
    </div>
  );
};

// ====== COMPOSANT PRINCIPAL ======
const DoctorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const dateFnsLocale = lang === 'ar' ? ar : fr;
  // Les categories de modeles de messages restent en francais cote donnees
  // (elles doivent correspondre exactement au champ "category" renvoye par
  // le backend pour que le filtre fonctionne) — seul le libelle affiche
  // dans l'optgroup est traduit via cette table de correspondance.
  const CATEGORY_LABEL_KEYS = {
    'Références': 'doc_cat_references',
    "Résultats d'analyse": 'doc_cat_analysis_results',
    'Résultats imagerie': 'doc_cat_imaging_results',
    'Avis médical': 'doc_cat_medical_opinion',
    'Suivi patient': 'doc_cat_patient_followup',
    'Urgences': 'doc_cat_urgent',
    'Général': 'doc_cat_general',
  };

  const [profile, setProfile] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [subscription, setSubscription] = useState(null);

  // ✅ États messagerie médicale
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [msgUnreadCount, setMsgUnreadCount] = useState(0);
  const [msgTab, setMsgTab] = useState('inbox');
  const [showCompose, setShowCompose] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [msgForm, setMsgForm] = useState({
    recipient_doctor_id: '',
    recipient_email: '',
    recipient_name: '',
    subject: '',
    body: '',
    message_type: 'general',
    patient_name: '',
    patient_age: '',
    priority: 'normal',
    attachment_name: '',
    attachment_data: '',
  });
  const [msgLoading, setMsgLoading] = useState(false);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [doctorResults, setDoctorResults] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [editSchedule, setEditSchedule] = useState({});
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [consultationDuration, setConsultationDuration] = useState('30');
  const [profileForm, setProfileForm] = useState({ phone: '', address: '', fees: '', bio: '', education: '', photo_url: '', languages: [], accepted_insurance: [], video_consultation_enabled: false, video_payment_ccp: '' });
  const [newLanguage, setNewLanguage] = useState('');
  const [newInsurance, setNewInsurance] = useState('');

  // Déplacement RDV
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleAppointment, setRescheduleAppointment] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Salle d'attente
  const [waitingRoom, setWaitingRoom] = useState([]);

  // Patients
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetail, setPatientDetail] = useState(null);
  const [patientDetailLoading, setPatientDetailLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(false);
  const [recordForm, setRecordForm] = useState({ allergies: [], antecedents: [], blood_type: '', chronic_conditions: [] });
  const [newNote, setNewNote] = useState('');
  const [newAllergie, setNewAllergie] = useState('');
  const [newAntecedent, setNewAntecedent] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [measurementForm, setMeasurementForm] = useState({ type: '', customType: '', value: '', unit: '', date: new Date().toISOString().split('T')[0], valueSystolic: '', valueDiastolic: '' });

  // Ajouter un patient hors plateforme
  const [manualPatientDialogOpen, setManualPatientDialogOpen] = useState(false);
  const [manualPatientForm, setManualPatientForm] = useState({ name: '', phone: '', email: '', date_of_birth: '' });
  const [manualPatientLoading, setManualPatientLoading] = useState(false);

  // ✅ NOUVEAU : Programmer un RDV depuis le dossier patient
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [bookForm, setBookForm] = useState({ date: '', time: '', reason: '', consultationType: 'in_person' });
  const [bookLoading, setBookLoading] = useState(false);

  // ✅ NOUVEAU : Supprimer un patient
  const [deletePatientDialogOpen, setDeletePatientDialogOpen] = useState(false);
  const [deletePatientLoading, setDeletePatientLoading] = useState(false);
  const [ordonnanceDialogOpen, setOrdonnanceDialogOpen] = useState(false);
  const [ordonnanceForm, setOrdonnanceForm] = useState({
    diagnostic: '',
    medications: [{ name: '', dosage: '', frequency: '', duration: '', dci_code: '', dci_name: '', forme: '' }],
    notes: '',
  });

  // Autocompletion medicament (registre officiel /api/medications, voir
  // backend/routes/medications.py) sur le champ "nom du medicament" de
  // l'ordonnance. Une seule liste de suggestions partagee entre toutes les
  // lignes (un seul champ est tape a la fois) : `medSuggestLine` indique a
  // quelle ligne elle s'applique actuellement.
  const [medSuggestions, setMedSuggestions] = useState([]);
  const [medSuggestLine, setMedSuggestLine] = useState(null);
  const medSuggestDebounceRef = useRef(null);

  // ✅ NOUVEAU (teleconsultation) : rejoindre une consultation video
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoJoinLoading, setVideoJoinLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const DAY_NAMES_MAP = { monday: t('day_monday'), tuesday: t('day_tuesday'), wednesday: t('day_wednesday'), thursday: t('day_thursday'), friday: t('day_friday'), saturday: t('day_saturday'), sunday: t('day_sunday') };

  const getAuthHeaders = () => {
    // Correctif B7 : le cookie httpOnly porte la session (withCredentials global).
    return {};
  };

  useEffect(() => {
    fetchData();
    fetchNotifications();
    fetchWaitingRoom();
    fetchPatients();
  }, []);

  // ✅ SSE : notifications en temps réel (remplace le polling 30s)
  useEffect(() => {
    // Correctif B7 : le jeton n'est plus accessible en JavaScript et ne doit
    // plus transiter dans l'URL (les query strings finissent dans les logs
    // d'acces, les logs proxy et l'en-tete Referer). EventSource joint le
    // cookie httpOnly via withCredentials.
    if (!isLoggedIn()) return;

    let eventSource = null;
    let fallbackInterval = null;

    const connectSSE = () => {
      try {
        eventSource = new EventSource(`${API_URL}/api/doctor/notifications/stream`, { withCredentials: true });

        eventSource.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            // Notification patient arrivé → alerte + refresh salle d'attente
            // Nouveau RDV pris par un patient → refresh liste RDV
            if (data.notification_type === 'new_appointment') {
              toast(t('doc_sse_new_appointment'), {
                description: data.message,
                duration: 8000,
                style: { background: '#0596DE', color: 'white' },
              });
              fetchData();
            }
            if (data.notification_type === 'patient_arrived') {
              toast(t('doc_sse_patient_arrived'), {
                description: data.message,
                duration: 8000,
                style: { background: '#10B981', color: 'white' },
              });
              fetchWaitingRoom();
            }
            // Refresh la cloche à chaque nouvelle notification
            fetchNotifications();
          } catch (_) {}
        };

        eventSource.onerror = () => {
          if (eventSource) eventSource.close();
          // Fallback polling si SSE échoue
          if (!fallbackInterval) {
            fallbackInterval = setInterval(() => {
              fetchNotifications();
              fetchWaitingRoom();
            }, 15000);
          }
        };
      } catch (_) {
        // SSE non supporté → polling
        fallbackInterval = setInterval(() => {
          fetchNotifications();
          fetchWaitingRoom();
        }, 15000);
      }
    };

    connectSSE();

    return () => {
      if (eventSource) eventSource.close();
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, []);

  const fetchData = async () => {
    try {
      const config = { withCredentials: true, headers: getAuthHeaders() };
      const [profileRes, appointmentsRes, subscriptionRes] = await Promise.all([
        axios.get(`${API_URL}/api/doctor/profile`, config),
        axios.get(`${API_URL}/api/doctor/appointments`, config),
        axios.get(`${API_URL}/api/subscription/status`, config).catch(() => ({ data: null })),
      ]);
      setProfile(profileRes.data);
      setEditSchedule(profileRes.data.schedule || {});
      // Correctif : ce reglage n'etait jamais relu depuis le profil — il
      // revenait systematiquement a 30 min a chaque chargement, meme apres
      // sauvegarde, et les heures de fin de RDV affichees etaient donc fausses.
      setConsultationDuration(profileRes.data.consultation_duration?.toString() || '30');
      setProfileForm({ phone: profileRes.data.phone || '', address: profileRes.data.address || '', fees: profileRes.data.fees?.toString() || '', bio: profileRes.data.bio || '', education: profileRes.data.education || '', photo_url: profileRes.data.photo_url || '', languages: profileRes.data.languages || [], accepted_insurance: profileRes.data.accepted_insurance || [], video_consultation_enabled: profileRes.data.video_consultation_enabled || false, video_payment_ccp: profileRes.data.video_payment_ccp || '' });
      setAppointments(appointmentsRes.data);
      setSubscription(subscriptionRes.data);

      // Charger messages et templates
      fetchMessages();
      fetchTemplates();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/doctor/notifications`, { withCredentials: true, headers: getAuthHeaders() });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {}
  };

  const fetchWaitingRoom = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/doctor/waiting-room`, { withCredentials: true, headers: getAuthHeaders() });
      setWaitingRoom(res.data || []);
    } catch (e) {}
  };

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/doctor/patients`, { withCredentials: true, headers: getAuthHeaders() });
      setPatients(res.data || []);
    } catch (e) {}
  };

  const handleAddManualPatient = async () => {
    if (!manualPatientForm.name.trim()) { toast.error(t('doc_name_required')); return; }
    setManualPatientLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/doctor/patients/manual`, manualPatientForm, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_patient_added'));
      setManualPatientDialogOpen(false);
      await fetchPatients();
      setSelectedPatient(res.data.patient_id);
      fetchPatientDetail(res.data.patient_id);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : t('doc_err_add_patient'));
    } finally {
      setManualPatientLoading(false);
    }
  };

  const fetchPatientDetail = async (patientId) => {
    setPatientDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/doctor/patients/${patientId}`, { withCredentials: true, headers: getAuthHeaders() });
      setPatientDetail(res.data);
      const record = res.data.medical_record || {};
      setRecordForm({ allergies: record.allergies || [], antecedents: record.antecedents || [], blood_type: record.blood_type || '', chronic_conditions: record.chronic_conditions || [] });
    } catch (e) {
      toast.error(t('doc_err_load_record'));
    } finally {
      setPatientDetailLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/doctor/notifications/read-all`, {}, { withCredentials: true, headers: getAuthHeaders() });
      fetchNotifications();
    } catch (e) {}
  };

  const handleMarkRead = async (notifId) => {
    try {
      await axios.put(`${API_URL}/api/doctor/notifications/${notifId}/read`, {}, { withCredentials: true, headers: getAuthHeaders() });
      fetchNotifications();
    } catch (e) {}
  };

  const handleCallPatient = async (appointmentId) => {
    try {
      await axios.put(`${API_URL}/api/doctor/waiting-room/${appointmentId}/call`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_patient_called'));
      fetchWaitingRoom();
    } catch (e) {
      toast.error(t('doc_error_generic'));
    }
  };

  const handlePatientDone = async (appointmentId) => {
    try {
      await axios.put(`${API_URL}/api/doctor/waiting-room/${appointmentId}/done`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_consultation_done'));
      fetchWaitingRoom();
      fetchData();
    } catch (e) {
      toast.error(t('doc_error_generic'));
    }
  };

  const handleSaveRecord = async () => {
    try {
      await axios.put(`${API_URL}/api/doctor/patients/${selectedPatient}/record`, recordForm, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_record_updated'));
      setEditingRecord(false);
      fetchPatientDetail(selectedPatient);
    } catch (e) {
      toast.error(t('doc_err_save'));
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API_URL}/api/doctor/patients/${selectedPatient}/notes`, { content: newNote }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_note_added'));
      setNewNote('');
      fetchPatientDetail(selectedPatient);
    } catch (e) {
      toast.error(t('doc_error_generic'));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API_URL}/api/doctor/patients/${selectedPatient}/notes/${noteId}`, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_note_deleted'));
      fetchPatientDetail(selectedPatient);
    } catch (e) {
      toast.error(t('doc_error_generic'));
    }
  };

  const handleAddMeasurement = async () => {
    const type = measurementForm.type === '__custom__' ? measurementForm.customType.trim() : measurementForm.type;
    // La tension arterielle se saisit en 2 champs separes (systolique/diastolique)
    // et est recombinee ici en "120/80" pour rester compatible avec le format
    // deja stocke en base (une simple chaine de texte).
    const isBloodPressure = measurementForm.type === 'tension_arterielle';
    const value = isBloodPressure
      ? (measurementForm.valueSystolic.trim() && measurementForm.valueDiastolic.trim()
        ? `${measurementForm.valueSystolic.trim()}/${measurementForm.valueDiastolic.trim()}`
        : '')
      : measurementForm.value.trim();
    if (!type || !value) { toast.error(t('doc_type_value_required')); return; }
    try {
      await axios.post(`${API_URL}/api/doctor/patients/${selectedPatient}/measurements`, {
        type, value, unit: measurementForm.unit.trim(), date: measurementForm.date, note: ''
      }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_measurement_added'));
      setMeasurementForm({ type: '', customType: '', value: '', unit: '', date: new Date().toISOString().split('T')[0], valueSystolic: '', valueDiastolic: '' });
      fetchPatientDetail(selectedPatient);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : t('doc_err_add_measurement'));
    }
  };

  const handleDeleteMeasurement = async (measurementId) => {
    try {
      await axios.delete(`${API_URL}/api/doctor/patients/${selectedPatient}/measurements/${measurementId}`, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_measurement_deleted'));
      fetchPatientDetail(selectedPatient);
    } catch (e) {
      toast.error(t('doc_error_generic'));
    }
  };

  // Correctif : les popups "programmer un RDV" et "déplacer un RDV"
  // proposaient TOUS les créneaux fixes (TIME_SLOTS), sans tenir compte du
  // planning réel du médecin ni des RDV déjà pris ce jour-là. L'index unique
  // en base rattrape un vrai doublon avec une erreur, mais rien n'avertissait
  // le médecin *avant* qu'il clique. Cette fonction ne fait que filtrer
  // l'affichage — la vérification qui compte reste côté serveur.
  //
  // Correctif L-PASTSLOT-DOCDASH (signale par Nadjib) : pour la date du
  // jour, cette liste continuait a proposer les creneaux DEJA PASSES (ex:
  // 09:00 alors qu'il est 15h) — le medecin pouvait les selectionner dans le
  // menu deroulant. La creation etait quand meme bloquee cote serveur (voir
  // routes/appointments.py, "Impossible de programmer un rendez-vous dans le
  // passe"), mais laisser l'option selectionnable donnait l'impression
  // trompeuse que ca allait fonctionner. On filtre desormais aussi par
  // l'heure actuelle quand la date choisie est aujourd'hui.
  const _WEEKDAYS_EN = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const getAvailableSlotsForDate = (dateStr, excludeAppointmentId = null) => {
    if (!dateStr) return TIME_SLOTS;
    const parsed = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return TIME_SLOTS;
    const weekday = _WEEKDAYS_EN[parsed.getDay()];
    const allowed = profile?.schedule?.[weekday] || [];
    const bookedTimes = new Set(
      (appointments || [])
        .filter((a) => a.date === dateStr
          && ['pending', 'confirmed'].includes(a.status)
          && a.appointment_id !== excludeAppointmentId)
        .map((a) => a.time)
    );
    // Correctif fuseau horaire (signale par Nadjib, 08/09/2026) : toujours
    // comparer a la date/heure REELLES en Algerie, pas a celles du
    // navigateur du visiteur — voir lib/algeriaTime.js.
    const isToday = dateStr === algeriaTodayYMD();
    const nowTime = algeriaNowHHMM();
    return allowed.filter((slot) => !bookedTimes.has(slot) && (!isToday || slot > nowTime));
  };

  // ✅ NOUVEAU : Programmer un RDV depuis le dossier patient
  const handleBookAppointment = async () => {
    if (!bookForm.date || !bookForm.time) {
      toast.error(t('doc_choose_date_time'));
      return;
    }
    setBookLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/doctor/appointments`,
        { patient_id: selectedPatient, date: bookForm.date, time: bookForm.time, reason: bookForm.reason, consultation_type: bookForm.consultationType },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success(t('doc_appointment_booked'));
      setBookDialogOpen(false);
      setBookForm({ date: '', time: '', reason: '', consultationType: 'in_person' });
      fetchData();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : t('doc_err_booking'));
      // Correctif : en cas d'echec (ex: creneau pris entre-temps), la grille
      // de creneaux disponibles restait figee sur l'ancien etat local et le
      // medecin pouvait retenter le meme creneau indefiniment.
      fetchData();
    } finally {
      setBookLoading(false);
    }
  };

  // ✅ NOUVEAU : Supprimer un patient de la liste
  const handleDeletePatient = async () => {
    setDeletePatientLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/doctor/patients/${selectedPatient}`,
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success(t('doc_patient_removed'));
      setDeletePatientDialogOpen(false);
      setSelectedPatient(null);
      setPatientDetail(null);
      fetchPatients();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : t('doc_err_delete'));
    } finally {
      setDeletePatientLoading(false);
    }
  };

  const handlePrintOrdonnance = async () => {
    const validMeds = ordonnanceForm.medications.filter((m) => m.name.trim());
    if (validMeds.length === 0) { toast.error(t('doc_add_at_least_one_med')); return; }
    const medIncomplete = validMeds.some((m) => !m.dosage.trim() || !m.frequency.trim());
    if (medIncomplete) { toast.error(t('doc_dosage_freq_required')); return; }

    // === Etape 1 : appeler le backend pour obtenir un token + URL de verification ===
    // Correctif (idee CGM) : on transmet aussi le patient et le contenu de
    // l'ordonnance pour qu'une copie privee soit versee dans son espace, en
    // plus de l'impression - voir routes/prescriptions.py. Best-effort cote
    // backend : ca n'empeche jamais l'impression si ca echoue (patient hors
    // plateforme, stockage indisponible...).
    let prescriptionToken = '';
    let verifyUrl = '';
    let qrDataUrl = '';
    let savedToPatientSpace = false;
    try {
      const issueRes = await axios.post(
        `${API_URL}/api/prescriptions/issue`,
        {
          drugs_count: validMeds.length,
          patient_id: selectedPatient || undefined,
          diagnostic: ordonnanceForm.diagnostic || undefined,
          medications: validMeds.map((m) => ({ name: m.name, dosage: m.dosage, frequency: m.frequency, duration: m.duration })),
          notes: ordonnanceForm.notes || undefined,
        },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      prescriptionToken = issueRes.data.token;
      verifyUrl = issueRes.data.verify_url;
      savedToPatientSpace = Boolean(issueRes.data.saved_to_patient_space);
      qrDataUrl = await QRCode.toDataURL(verifyUrl, { width: 140, margin: 1 });
    } catch (err) {
      toast.error(t('doc_err_generate_cert'));
      return;
    }

    // === Etape 2 : construire le HTML de l'ordonnance avec le QR code ===
    const printWindow = window.open('', '_blank');
    const today = format(new Date(), 'dd/MM/yyyy');
    const todayTime = format(new Date(), 'dd/MM/yyyy HH:mm');
    const medsHtml = validMeds.map((med, i) => {
      const info = [med.dosage ? 'Dosage : ' + med.dosage : '', med.frequency ? 'Fréquence : ' + med.frequency : '', med.duration ? 'Durée : ' + med.duration : ''].filter(Boolean).join(' | ');
      return '<div class="medication"><div class="med-number">' + (i + 1) + '</div><div><div class="med-name">' + esc(med.name) + '</div><div class="med-info">' + esc(info) + '</div></div></div>';
    }).join('');
    // Le diagnostic n'apparait PAS sur l'ordonnance imprimee/PDF (retire le
    // 2026-09-05, sur demande explicite de Nadjib, alignement sur la
    // pratique constatee sur Doctolib) : ce document est remis au patient
    // et vu par le pharmacien, parfois par un tiers venu chercher les
    // medicaments — la pathologie n'a pas a y figurer. Le champ reste
    // saisissable dans le formulaire et continue d'etre enregistre cote
    // backend (voir routes/prescriptions.py) pour le dossier interne du
    // patient sur la plateforme ; seul l'affichage sur le PDF change ici.
    const notesHtml = ordonnanceForm.notes ? '<div class="notes-section"><p><strong>Recommandations :</strong></p><p>' + esc(ordonnanceForm.notes) + '</p></div>' : '';
    const bloodType = patientDetail?.medical_record?.blood_type ? '<p><strong>Groupe sanguin :</strong> ' + esc(patientDetail.medical_record.blood_type) + '</p>' : '';
    const qrSection = '<div class="qr-section"><img src="' + qrDataUrl + '" alt="QR" /><div class="qr-info"><p><strong>N&deg; ' + esc(prescriptionToken) + '</strong></p><p>Verifier l\'authenticite : ' + esc(verifyUrl) + '</p><p style="color:#94a3b8;margin-top:4px">Scannez ce QR code pour verifier l\'ordonnance</p></div></div>';
    const html = '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Ordonnance ' + esc(prescriptionToken) + '</title><style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: Arial, sans-serif; padding: 40px; color: #1a1a1a; font-size: 14px; } .header { display: flex; justify-content: space-between; border-bottom: 3px solid #0596DE; padding-bottom: 20px; margin-bottom: 24px; } .doctor-info h2 { color: #0596DE; font-size: 20px; } .doctor-info p { color: #555; font-size: 13px; line-height: 1.6; } .header-right { text-align: right; } .logo { color: #0596DE; font-size: 18px; font-weight: bold; } .ord-num { font-size: 11px; color: #555; margin-top: 6px; font-family: monospace; } .title { text-align: center; font-size: 20px; font-weight: bold; letter-spacing: 3px; color: #0596DE; margin-bottom: 24px; border: 2px solid #0596DE; padding: 10px; } .patient-section { display: flex; justify-content: space-between; margin-bottom: 24px; background: #f8fafc; padding: 16px; border-radius: 8px; } .medications h3 { font-size: 16px; margin-bottom: 12px; color: #0596DE; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; } .medication { display: flex; gap: 12px; padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 8px; } .med-number { width: 26px; height: 26px; background: #0596DE; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; flex-shrink: 0; } .med-name { font-weight: bold; margin-bottom: 4px; } .med-info { color: #555; font-size: 12px; } .notes-section { background: #f0f9ff; border: 1px solid #bae6fd; padding: 12px; border-radius: 8px; margin-bottom: 16px; } .qr-section { display: flex; gap: 14px; align-items: center; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; margin-bottom: 16px; background: #f8fafc; } .qr-section img { width: 90px; height: 90px; flex-shrink: 0; } .qr-info { font-size: 11px; color: #555; line-height: 1.6; word-break: break-all; } .qr-info strong { color: #0596DE; font-family: monospace; font-size: 13px; } .footer { display: flex; justify-content: space-between; margin-top: 24px; border-top: 1px solid #e2e8f0; padding-top: 24px; } .line { width: 200px; border-bottom: 2px solid #1a1a1a; margin-bottom: 8px; } .tabbibak { font-size: 11px; color: #94a3b8; text-align: right; }</style></head><body><div class="header"><div class="doctor-info"><h2>Dr. ' + esc(profile?.name || '') + '</h2><p>' + esc(SPECIALTIES_MAP[profile?.specialty] || profile?.specialty || '') + '</p><p>' + esc(profile?.address || '') + '</p><p>Tel : ' + esc(profile?.phone || '') + '</p></div><div class="header-right"><div class="logo">Tabbibak.dz</div><div class="ord-num">N&deg; ' + esc(prescriptionToken) + '</div></div></div><div class="title">ORDONNANCE MÉDICALE</div><div class="patient-section"><div><p><strong>Patient :</strong> ' + esc(patientDetail?.name || '') + '</p><p><strong>Tel :</strong> ' + esc(patientDetail?.phone || '') + '</p></div><div><p><strong>Date :</strong> ' + today + '</p>' + bloodType + '</div></div>' + '<div class="medications"><h3>Prescription</h3>' + medsHtml + '</div>' + notesHtml + qrSection + '<div class="footer"><div><div class="line"></div><p>Signature et cachet du médecin</p><p style="font-weight:bold;margin-top:4px">Dr. ' + esc(profile?.name || '') + '</p></div><div class="tabbibak"><p>Généré via Tabbibak.dz</p><p>' + todayTime + '</p></div></div></body></html>';
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
    toast.success(savedToPatientSpace ? t('doc_prescription_saved_toast') : t('doc_prescription_ready'));
  };

  // dci_code/dci_name/forme : metadonnees du registre officiel, invisibles
  // pour le medecin, utilisees uniquement pour la verification de doublons
  // ci-dessous (voir rxDuplicateGroups/rxSameSubstanceGroups). Renseignees
  // seulement quand une suggestion est choisie (selectMedSuggestion) —
  // jamais quand le medecin tape a la main sans selectionner, puisqu'on ne
  // peut alors pas garantir a quel produit exact ca correspond.
  const addMedication = () => setOrdonnanceForm((prev) => ({ ...prev, medications: [...prev.medications, { name: '', dosage: '', frequency: '', duration: '', dci_code: '', dci_name: '', forme: '' }] }));
  const removeMedication = (index) => setOrdonnanceForm((prev) => ({ ...prev, medications: prev.medications.filter((_, i) => i !== index) }));
  const updateMedication = (index, field, value) => setOrdonnanceForm((prev) => { const meds = [...prev.medications]; meds[index] = { ...meds[index], [field]: value }; return { ...prev, medications: meds }; });

  // Le medecin a modifie a la main le nom ou le dosage d'une ligne deja
  // rattachee a une fiche du registre (dci_code/dci_name) : ce
  // rattachement n'est plus fiable (le dosage tape peut ne plus
  // correspondre au dci_code retenu, par exemple) — on l'efface plutot que
  // de risquer un faux rapprochement ou un vrai rapprochement manque. Le
  // medecin peut re-selectionner une suggestion pour le retablir.
  const invalidateMedRegistryMatch = (index) => setOrdonnanceForm((prev) => {
    const meds = [...prev.medications];
    meds[index] = { ...meds[index], dci_code: '', dci_name: '', forme: '' };
    return { ...prev, medications: meds };
  });

  // Recherche live sur le registre public de medicaments (min. 2 caracteres,
  // meme convention que backend/routes/medications.py et
  // pages/tools/DrugDuplicateChecker.js). Endpoint public, aucune
  // authentification necessaire.
  const fetchMedSuggestions = useCallback(async (query, lineIndex) => {
    if (query.trim().length < 2) {
      setMedSuggestions([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/medications`, { params: { search: query.trim(), limit: 8 } });
      setMedSuggestions(res.data?.medications || []);
      setMedSuggestLine(lineIndex);
    } catch {
      setMedSuggestions([]);
    }
  }, []);

  const handleMedNameChange = (index, value) => {
    updateMedication(index, 'name', value);
    invalidateMedRegistryMatch(index);
    if (medSuggestDebounceRef.current) clearTimeout(medSuggestDebounceRef.current);
    medSuggestDebounceRef.current = setTimeout(() => fetchMedSuggestions(value, index), 300);
  };

  // Meme logique que handleMedNameChange : un dosage tape a la main ne
  // correspond plus forcement au dci_code rattache par une suggestion
  // precedente (un dosage different = un dci_code different dans le
  // registre officiel).
  const handleMedDosageChange = (index, value) => {
    updateMedication(index, 'dosage', value);
    invalidateMedRegistryMatch(index);
  };

  const selectMedSuggestion = (index, med) => {
    setOrdonnanceForm((prev) => {
      const meds = [...prev.medications];
      // Ne pre-remplit le dosage que s'il est encore vide : ne jamais
      // ecraser une valeur deja saisie par le medecin.
      const hadDosage = !!meds[index].dosage;
      meds[index] = {
        ...meds[index],
        name: med.nom_commercial || meds[index].name,
        dosage: hadDosage ? meds[index].dosage : (med.dosage || ''),
        // dci_code/dci_name/forme ne sont rattaches que si le dosage vient
        // AUSSI de cette suggestion (donc etait vide avant) : si le
        // medecin avait deja tape un dosage different de celui de cette
        // fiche, on ne peut pas garantir que ce dosage correspond a ce
        // dci_code precis (un autre dosage = un autre dci_code dans le
        // registre) — mieux vaut ne pas rattacher que rattacher a tort.
        dci_code: hadDosage ? '' : (med.dci_code || ''),
        dci_name: hadDosage ? '' : (med.dci_name || ''),
        forme: hadDosage ? '' : (med.forme || ''),
      };
      return { ...prev, medications: meds };
    });
    setMedSuggestions([]);
    setMedSuggestLine(null);
  };

  // Verification de doublons sur l'ordonnance EN COURS DE REDACTION —
  // meme logique (deux niveaux) que le verificateur public cote patient
  // (pages/tools/DrugDuplicateChecker.js), voir utils/medicationDuplicates.js.
  // Purement informatif pour le medecin : n'empeche jamais d'imprimer ou
  // d'enregistrer l'ordonnance (une association de dosages peut etre
  // clinique et voulue). Ne porte que sur les lignes ou dci_code/dci_name
  // sont rattaches (voir selectMedSuggestion/invalidateMedRegistryMatch
  // ci-dessus) : une ligne tapee a la main sans selectionner de suggestion
  // n'est pas verifiee.
  const rxDuplicateGroups = useMemo(
    () => computeDuplicateGroups(ordonnanceForm.medications),
    [ordonnanceForm.medications]
  );
  const rxSameSubstanceGroups = useMemo(
    () => computeSameSubstanceGroups(ordonnanceForm.medications),
    [ordonnanceForm.medications]
  );

  // ✅ Fonctions messagerie
  const fetchMessages = async () => {
    try {
      const config = { withCredentials: true, headers: getAuthHeaders() };
      const [inboxRes, sentRes] = await Promise.all([
        axios.get(`${API_URL}/api/messages/inbox`, config),
        axios.get(`${API_URL}/api/messages/sent`, config),
      ]);
      setMessages(inboxRes.data.messages || []);
      setMsgUnreadCount(inboxRes.data.unread || 0);
      setSentMessages(sentRes.data.messages || []);
    } catch (e) { console.error(e); }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messages/templates`, { withCredentials: true, headers: getAuthHeaders() });
      setTemplates(res.data || []);
    } catch (e) { console.error(e); }
  };

  const searchDoctors = async (q) => {
    if (q.length < 2) { setDoctorResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/messages/doctors/search?q=${encodeURIComponent(q)}`, { withCredentials: true, headers: getAuthHeaders() });
      setDoctorResults(res.data || []);
    } catch (e) { setDoctorResults([]); }
  };

  const handleSelectTemplate = (templateId) => {
    const tpl = templates.find(t => t.id === templateId);
    if (tpl) {
      setMsgForm(prev => ({ ...prev, subject: tpl.subject, body: tpl.body }));
      setSelectedTemplate(templateId);
    }
  };

  const handleSendMessage = async () => {
    if (!msgForm.subject.trim() || !msgForm.body.trim()) {
      toast.error(t('doc_subject_message_required'));
      return;
    }
    if (!msgForm.recipient_doctor_id && !msgForm.recipient_email.trim()) {
      toast.error(t('doc_recipient_required'));
      return;
    }
    setMsgLoading(true);
    try {
      await axios.post(`${API_URL}/api/messages/send`, {
        ...msgForm,
        patient_age: msgForm.patient_age ? parseInt(msgForm.patient_age) : null,
        recipient_doctor_id: msgForm.recipient_doctor_id || null,
        recipient_email: msgForm.recipient_email || null,
      }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_message_sent'));
      setShowCompose(false);
      setMsgForm({ recipient_doctor_id: '', recipient_email: '', recipient_name: '', subject: '', body: '', message_type: 'general', patient_name: '', patient_age: '', priority: 'normal', attachment_name: '', attachment_data: '' });
      setSelectedTemplate('');
      setDoctorSearch('');
      setDoctorResults([]);
      fetchMessages();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : t('doc_err_send_message'));
    } finally {
      setMsgLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('doc_file_too_large'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setMsgForm(prev => ({
        ...prev,
        attachment_name: file.name,
        attachment_data: ev.target.result.split(',')[1],
      }));
      toast.success(`${t('doc_file_added_prefix')} "${file.name}" ${t('doc_file_added_suffix')}`);
    };
    reader.readAsDataURL(file);
  };

  const handleMarkMsgRead = async (messageId) => {
    try {
      await axios.put(`${API_URL}/api/messages/${messageId}/read`, {}, { withCredentials: true, headers: getAuthHeaders() });
      fetchMessages();
    } catch (e) { console.error(e); }
  };
  const handleReply = (msg) => {
  setMsgForm(prev => ({ ...prev, recipient_doctor_id: msg.sender_doctor_id || '', recipient_email: msg.sender_doctor_id ? '' : (msg.recipient_email || ''), recipient_name: msg.sender_name || '', subject: `RE: ${msg.subject}`, body: `\n\n---\n${t('doc_original_message_prefix')} ${msg.sender_name} :\n${msg.body}`, patient_name: msg.patient_name || '', patient_age: msg.patient_age || '', priority: 'normal', attachment_name: '', attachment_data: '' }));
  setSelectedTemplate(''); setDoctorSearch(msg.sender_name || ''); setShowCompose(true); setSelectedMsg(null);
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

  const handleDeleteMsg = async (messageId, e) => {
    e.stopPropagation();
    if (!window.confirm(t('doc_confirm_delete_message'))) return;
    try {
      await axios.delete(`${API_URL}/api/messages/${messageId}`, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_message_deleted'));
      fetchMessages();
    } catch (err) { toast.error(t('doc_err_deletion')); }
  };

  const handlePayClick = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/subscription/checkout`, {}, { withCredentials: true, headers: getAuthHeaders() });
      if (res.data?.checkout_url) window.open(res.data.checkout_url, '_blank');
    } catch (error) {
      toast.info(t('doc_use_bank_transfer_notice'));
    }
  };

  const handleStatusChange = async () => {
    if (!selectedAppointment || !newStatus) return;
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/doctor/appointments/${selectedAppointment.appointment_id}/status`, { status: newStatus }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('OK');
      setStatusDialogOpen(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRescheduleOpen = (appointment) => {
    setRescheduleAppointment(appointment);
    setRescheduleDate(appointment.date);
    setRescheduleTime(appointment.time);
    setRescheduleReason('');
    setRescheduleDialogOpen(true);
  };

  // ✅ NOUVEAU (teleconsultation) : le jeton de reunion est cree a la demande,
  // juste avant de rejoindre (jamais stocke) — voir daily_video.py et
  // routes/appointments.py (GET /api/appointments/{id}/video-token).
  const handleJoinVideo = async (appointment) => {
    setVideoJoinLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/appointments/${appointment.appointment_id}/video-token`, { withCredentials: true, headers: getAuthHeaders() });
      setVideoUrl(res.data.url);
      setVideoDialogOpen(true);
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('doc_video_join_error'));
    } finally {
      setVideoJoinLoading(false);
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleDate || !rescheduleTime) { toast.error(t('doc_choose_date_time')); return; }
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/doctor/appointments/${rescheduleAppointment.appointment_id}/reschedule`, { date: rescheduleDate, time: rescheduleTime, reason: rescheduleReason }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('doc_appointment_rescheduled'));
      setRescheduleDialogOpen(false);
      setRescheduleAppointment(null);
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('doc_err_reschedule'));
      // Correctif : meme raison que sur handleBookAppointment — sans ce
      // rafraichissement, la grille de creneaux ne refletait pas le conflit
      // qui vient de causer l'echec.
      fetchData();
    } finally {
      setActionLoading(false);
    }
  };

  const handleScheduleUpdate = async () => {
    // Correctif : retirer un créneau récurrent du planning n'annule PAS les
    // RDV déjà pris sur ce créneau à une date future précise — et c'est
    // volontaire, on ne veut jamais annuler un RDV automatiquement. Mais
    // rien n'avertissait le médecin qu'un patient était déjà confirmé sur un
    // horaire qu'il vient de fermer ; il pouvait l'oublier sans le vouloir.
    const removedSlots = [];
    const oldSchedule = profile?.schedule || {};
    Object.keys(oldSchedule).forEach((day) => {
      (oldSchedule[day] || []).forEach((slot) => {
        // Correctif : comparait par egalite stricte (.includes(slot)), ce
        // qui fonctionnait tant qu'un creneau restait une simple chaine -
        // mais un creneau reste "encore propose" meme si ses TYPES ont
        // change (setSlotTypes cree un nouvel objet). On compare desormais
        // par l'heure du creneau, pas par la reference/valeur brute.
        const t = slotEntryTime(slot);
        if (!(editSchedule[day] || []).some((s) => slotEntryTime(s) === t)) removedSlots.push({ day, slot: t });
      });
    });
    if (removedSlots.length > 0) {
      // Correctif fuseau horaire : date du jour reelle en Algerie, pas
      // celle du navigateur du medecin — voir lib/algeriaTime.js.
      const todayStr = algeriaTodayYMD();
      const affected = (appointments || []).filter((a) => {
        if (!['pending', 'confirmed'].includes(a.status) || a.date < todayStr) return false;
        const parsed = new Date(`${a.date}T00:00:00`);
        if (Number.isNaN(parsed.getTime())) return false;
        const weekday = _WEEKDAYS_EN[parsed.getDay()];
        return removedSlots.some((r) => r.day === weekday && r.slot === a.time);
      });
      if (affected.length > 0) {
        const noms = affected.map((a) => `${a.patient?.name || t('doc_patient_fallback')} (${a.date} ${t('doc_at_time')} ${a.time})`).join(', ');
        const confirmMsg = `${t('doc_schedule_warning_prefix')} ${affected.length} ${t('doc_schedule_warning_mid')} ${noms}. ${t('doc_schedule_warning_suffix')}`;
        if (!window.confirm(confirmMsg)) return;
      }
    }
    setActionLoading(true);
    try {
      // Correctif : la duree de consultation choisie dans ce meme onglet
      // n'etait jamais envoyee au serveur — seul "schedule" partait dans la
      // requete. Le reglage semblait sauvegarde mais revenait a 30 min des
      // le prochain chargement.
      await axios.put(
        `${API_URL}/api/doctor/profile`,
        { schedule: editSchedule, consultation_duration: parseInt(consultationDuration, 10) },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success('OK');
      setEditingSchedule(false);
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    if (!isValidCcp(profileForm.video_payment_ccp)) {
      toast.error(t('doc_video_payment_ccp_invalid'));
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/doctor/profile`, { ...profileForm, fees: parseInt(profileForm.fees, 10) || 0 }, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('OK');
      setEditingProfile(false);
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSlot = (day, slot) => {
    setEditSchedule((prev) => {
      const daySlots = prev[day] || [];
      const exists = daySlots.some((s) => slotEntryTime(s) === slot);
      if (exists) {
        return { ...prev, [day]: daySlots.filter((s) => slotEntryTime(s) !== slot) };
      }
      // Correctif : un nouveau creneau coche demarre toujours en "cabinet
      // seul" par defaut (jamais "video" implicitement) - le medecin doit
      // explicitement l'ouvrir a la video via les boutons sous le creneau.
      const newEntry = { time: slot, types: ['in_person'] };
      return {
        ...prev,
        [day]: [...daySlots, newEntry].sort((a, b) => slotEntryTime(a).localeCompare(slotEntryTime(b))),
      };
    });
  };

  // Correctif (demande Nadjib, 20/09/2026) : change les types de
  // consultation autorises pour UN creneau precis deja coche (cabinet /
  // video / les deux), sans toucher aux autres creneaux du jour.
  const setSlotTypes = (day, slot, types) => {
    setEditSchedule((prev) => {
      const daySlots = prev[day] || [];
      return {
        ...prev,
        [day]: daySlots.map((s) => (slotEntryTime(s) === slot ? { time: slot, types } : s)),
      };
    });
  };

  const addLanguage = (language) => { if (language && !profileForm.languages.includes(language)) setProfileForm((prev) => ({ ...prev, languages: [...prev.languages, language] })); setNewLanguage(''); };
  const removeLanguage = (language) => setProfileForm((prev) => ({ ...prev, languages: prev.languages.filter((l) => l !== language) }));
  const addInsurance = (insurance) => { if (insurance && !profileForm.accepted_insurance.includes(insurance)) setProfileForm((prev) => ({ ...prev, accepted_insurance: [...prev.accepted_insurance, insurance] })); setNewInsurance(''); };
  const removeInsurance = (insurance) => setProfileForm((prev) => ({ ...prev, accepted_insurance: prev.accepted_insurance.filter((i) => i !== insurance) }));

  // Correctif fuseau horaire : "aujourd'hui" reel en Algerie, pas celui du
  // navigateur du medecin — voir lib/algeriaTime.js.
  const todayAppointments = appointments.filter((apt) => format(parseISO(apt.date), 'yyyy-MM-dd') === algeriaTodayYMD() && ['pending', 'confirmed'].includes(apt.status));
  const upcomingAppointments = appointments.filter((apt) => ['pending', 'confirmed'].includes(apt.status) && !isBefore(parseISO(apt.date), startOfDay(new Date())));
  const handleEditAppointment = (appointment) => { setSelectedAppointment(appointment); setNewStatus(appointment.status); setStatusDialogOpen(true); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            <div className="grid md:grid-cols-3 gap-6">{[1,2,3].map((i) => <div key={i} className="bg-white rounded-2xl p-6 h-32" />)}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8 text-center"><h2 className="text-xl font-semibold text-[#0F172A]">{t('doc_title')}</h2></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="doctor-dashboard">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] font-['Outfit']">{t('doc_title')}</h1>
            <p className="text-[#64748B] mt-1">{t('doc_subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell notifications={notifications} unreadCount={unreadCount} onMarkAllRead={handleMarkAllRead} onMarkRead={handleMarkRead} />
            {profile.status === 'pending' && <span className="px-4 py-2 bg-[#FEF3C7] text-[#F59E0B] rounded-full font-medium">{t('doc_pending_approval')}</span>}
            {profile.status === 'approved' && <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#D1FAE5] text-[#10B981] rounded-full font-medium"><span className="w-2 h-2 rounded-full bg-[#10B981]" />{t('doc_active')}</span>}
          </div>
        </div>

        <SubscriptionBanner subscription={subscription} onPayClick={handlePayClick} />

        {/* Correctif : tant que le compte n'est pas approuve par l'admin, on ne
            montre plus les statistiques du cabinet (tout est a 0 de toute
            facon) ni les onglets operationnels — seul "Mon profil" et
            "Abonnement" restent accessibles, pour eviter qu'un compte non
            valide puisse explorer/copier l'ensemble des fonctionnalites. */}
        {profile.status === 'approved' ? (
          <div className="grid md:grid-cols-6 gap-3 mb-8">
            <CompactMetricCard icon={Clock} label={t('doc_today')} value={todayAppointments.length} color="#0596DE" />
            <CompactMetricCard icon={CheckCircle} label={t('doc_metric_confirmed')} value={appointments.filter((a) => a.status === 'confirmed').length} color="#10B981" />
            <CompactMetricCard icon={AlertCircle} label={t('doc_pending_count')} value={appointments.filter((a) => a.status === 'pending' && !isBefore(parseISO(a.date), startOfDay(new Date()))).length} color="#F59E0B" />
            <CompactMetricCard icon={TrendingUp} label={t('doc_metric_revenue_month')} value={`${Math.round(appointments.filter((a) => isAfter(parseISO(a.date), startOfMonth(new Date()))).length * (profile.fees || 0)).toLocaleString()}`} color="#059669" subtext="DZD" />
            <CompactMetricCard icon={Users} label={t('doc_metric_total_patients')} value={new Set(appointments.map((a) => a.patient_id)).size} color="#7C3AED" />
            <CompactMetricCard icon={Star} label={t('doc_metric_avg_rating')} value={`${profile.average_rating || 4.5}/5`} color="#EF4444" subtext={`${profile.review_count || 0} ${t('doc_rating')}`} />
          </div>
        ) : (
          <div className="mb-8 p-6 bg-white rounded-2xl border border-[#E2E8F0] text-center text-[#64748B]">
            {t('doc_account_pending_notice')}
          </div>
        )}

        {/* ✅ NOUVEAU (teleconsultation) : acces rapide sur le tableau de bord
            principal, plutot que seulement dans l'onglet "Mon profil". */}
        {profile.status === 'approved' && (
          <TeleconsultationQuickSettings profile={profile} onSaved={fetchData} />
        )}

        {/* Onglets */}
        <Tabs defaultValue={profile.status === 'approved' ? "appointments" : "profile"} className="space-y-6">
          <TabsList className="bg-white rounded-full p-1 border border-[#E2E8F0] overflow-x-auto flex-nowrap">
            {profile.status === 'approved' && (
              <>
                <TabsTrigger value="appointments" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">{t('doc_tab_appointments')}</TabsTrigger>
                <TabsTrigger value="waiting" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
                  {t('doc_tab_waiting_room')}
                  {waitingRoom.length > 0 && <span className="ml-1 w-5 h-5 bg-[#10B981] text-white text-xs rounded-full inline-flex items-center justify-center">{waitingRoom.length}</span>}
                </TabsTrigger>
                <TabsTrigger value="patients" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">{t('doc_tab_patients')}</TabsTrigger>
                <TabsTrigger value="analytics" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">{t('doc_tab_analytics')}</TabsTrigger>
                <TabsTrigger value="schedule" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">{t('doc_tab_schedule')}</TabsTrigger>
              </>
            )}
            <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">{t('doc_tab_profile')}</TabsTrigger>
            {profile.status === 'approved' && (
              <TabsTrigger value="messages" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white relative">
                <MessageSquare className="w-4 h-4 mr-1" />
                {t('doc_tab_messages')}
                {msgUnreadCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#EF4444] text-white text-xs rounded-full flex items-center justify-center">{msgUnreadCount}</span>}
              </TabsTrigger>
            )}
            <TabsTrigger value="subscription" className="rounded-full data-[state=active]:bg-[#0596DE] data-[state=active]:text-white">
              <CreditCard className="w-4 h-4 mr-1" />{t('doc_tab_subscription')}
              {subscription && !subscription.is_active && <span className="ml-1 w-2 h-2 rounded-full bg-[#EF4444] inline-block" />}
            </TabsTrigger>
          </TabsList>

          {/* TAB 1 : RENDEZ-VOUS */}
          <TabsContent value="appointments">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-4">{t('doc_upcoming')}</h3>
              {upcomingAppointments.length === 0 ? (
                <div className="text-center py-8 text-[#64748B]"><Calendar className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" /><p>{t('doc_no_upcoming')}</p></div>
              ) : (
                <div className="space-y-3">
                  {upcomingAppointments.map((apt) => (
                    <EnhancedAppointmentRow key={apt.appointment_id} appointment={apt} profile={profile} dateFnsLocale={dateFnsLocale} t={t} onEdit={handleEditAppointment} onReschedule={handleRescheduleOpen} onJoinVideo={handleJoinVideo} videoJoinLoading={videoJoinLoading} consultationDuration={parseInt(consultationDuration)} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB 2 : SALLE D'ATTENTE */}
          <TabsContent value="waiting">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#0F172A]">{t('doc_waiting_room_title')}</h3>
                <Button variant="outline" size="sm" onClick={fetchWaitingRoom}>{t('doc_refresh_btn')}</Button>
              </div>
              {waitingRoom.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  <Users className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
                  <p>{t('doc_no_patient_waiting')}</p>
                  <p className="text-xs mt-1">{t('doc_patients_signal_arrival')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {waitingRoom.map((apt) => (
                    <div key={apt.appointment_id} className={`flex items-center justify-between p-4 rounded-xl border-l-4 ${apt.waiting_status === 'in_consultation' ? 'bg-[#D1FAE5] border-[#10B981]' : 'bg-[#FEF3C7] border-[#F59E0B]'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${apt.waiting_status === 'in_consultation' ? 'bg-[#10B981]' : 'bg-[#F59E0B]'}`}>
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          {/* Correctif (demande Nadjib, 20/09/2026) : un patient en
                              teleconsultation se signale desormais dans cette meme
                              salle d'attente (il est chez lui, pas dans le couloir) -
                              sans ce badge le medecin l'attendrait physiquement. */}
                          <h4 className="font-medium text-[#0F172A] flex items-center gap-2">
                            {apt.patient?.name || '-'}
                            {apt.consultation_type === 'video' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0596DE] text-white text-[10px] font-medium">
                                <Video className="w-3 h-3" />{t('doc_waiting_video_badge')}
                              </span>
                            )}
                          </h4>
                          <p className="text-xs text-[#64748B]">{apt.patient?.phone} — {t('doc_appointment_abbr')} {apt.time}</p>
                          <p className="text-xs text-[#64748B]">
                            {apt.waiting_status === 'in_consultation' ? t('doc_in_consultation') : t('doc_badge_pending')}
                            {apt.arrived_at && ` — ${t('doc_arrived_prefix')} ${format(parseISO(apt.arrived_at), 'HH:mm')}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {/* Sans ce bouton, le medecin voit son patient video en
                            attente mais doit repartir dans l'onglet Agenda pour
                            lancer l'appel. Meme handler que l'agenda, et le
                            serveur revalide la fenetre horaire de toute facon
                            (routes/appointments.py, get_video_token). */}
                        {apt.consultation_type === 'video' && (
                          <Button size="sm" onClick={() => handleJoinVideo(apt)} disabled={videoJoinLoading} className="bg-[#0596DE] text-white">
                            <Video className="w-3 h-3 mr-1" />{t('doc_join_video_btn')}
                          </Button>
                        )}
                        {apt.waiting_status === 'arrived' && (
                          <Button size="sm" onClick={() => handleCallPatient(apt.appointment_id)} className="bg-[#0596DE] text-white">
                            {t('doc_call_patient_btn')}
                          </Button>
                        )}
                        {apt.waiting_status === 'in_consultation' && (
                          <Button size="sm" onClick={() => handlePatientDone(apt.appointment_id)} className="bg-[#10B981] text-white">
                            {t('doc_finish_btn')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB 3 : PATIENTS */}
          <TabsContent value="patients">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Liste patients */}
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#0F172A]">{t('doc_my_patients_prefix')} ({patients.length})</h3>
                  <Button size="sm" variant="outline" onClick={() => { setManualPatientForm({ name: '', phone: '', email: '', date_of_birth: '' }); setManualPatientDialogOpen(true); }}>
                    <Plus className="w-3 h-3 mr-1" />{t('doc_add_btn')}
                  </Button>
                </div>
                {patients.length === 0 ? (
                  <div className="text-center py-8 text-[#64748B] text-sm"><Users className="w-8 h-8 mx-auto mb-2 text-[#E2E8F0]" /><p>{t('doc_no_patient_seen')}</p></div>
                ) : (
                  <div className="space-y-2">
                    {patients.map((patient) => (
                      <button
                        key={patient.user_id}
                        onClick={() => { setSelectedPatient(patient.user_id); fetchPatientDetail(patient.user_id); }}
                        className={`w-full text-left p-3 rounded-xl transition-colors flex items-center justify-between ${selectedPatient === patient.user_id ? 'bg-[#E6F4FB] border border-[#0596DE]' : 'hover:bg-[#F8FAFC]'}`}
                      >
                        <div>
                          <p className="font-medium text-sm text-[#0F172A]">{patient.name}{patient.is_manual && <span className="ml-1 text-[10px] text-[#94A3B8]">{t('doc_offplatform_paren')}</span>}</p>
                          <p className="text-xs text-[#64748B]">{patient.appointment_count} {patient.appointment_count > 1 ? t('doc_visits_word') : t('doc_visit_word')}</p>
                          {patient.last_appointment && <p className="text-xs text-[#94A3B8]">{t('doc_last_visit_label')} {patient.last_appointment}</p>}
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Détail patient */}
              <div className="md:col-span-2">
                {!selectedPatient ? (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center text-[#64748B]">
                    <Activity className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
                    <p>{t('doc_select_patient_notice')}</p>
                  </div>
                ) : patientDetailLoading ? (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-12 text-center text-[#64748B]">{t('doc_loading')}</div>
                ) : patientDetail ? (
                  <div className="space-y-4">
                    {/* Info patient + actions */}
                    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-[#E6F4FB] flex items-center justify-center">
                            <span className="text-xl font-bold text-[#0596DE]">{patientDetail.name?.charAt(0)}</span>
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0F172A]">{patientDetail.name}{patientDetail.is_manual && <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#64748B] align-middle">{t('doc_offplatform_badge')}</span>}</h3>
                            <p className="text-sm text-[#64748B]">{patientDetail.phone}</p>
                            {(() => {
                              // Correctif : s'appuyer sur le vrai statut "no_show" (passe
                              // automatiquement 2h apres l'heure du RDV si aucune presence n'a ete signalee, cote backend) plutot
                              // que sur une heuristique de date, qui marquait a tort tout RDV
                              // passe non cloture comme non honore.
                              const noShowCount = (patientDetail.appointments || []).filter(
                                (a) => a.status === 'no_show'
                              ).length;
                              return noShowCount > 0 ? (
                                <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#FEE2E2] text-[#EF4444]">
                                  ⚠ {noShowCount} {noShowCount > 1 ? t('doc_no_show_appointments_plural') : t('doc_no_show_appointment_singular')}
                                </span>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        {/* ✅ NOUVEAU : Boutons d'action patient */}
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            onClick={() => { setBookForm({ date: '', time: '', reason: '', consultationType: 'in_person' }); setBookDialogOpen(true); }}
                            className="bg-[#0596DE] hover:bg-[#047CBA] text-white"
                          >
                            <Plus className="w-3 h-3 mr-1" />{t('doc_book_appointment_btn')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeletePatientDialogOpen(true)}
                            className="text-red-500 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />{t('doc_delete_btn')}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setOrdonnanceForm({ diagnostic: '', medications: [{ name: '', dosage: '', frequency: '', duration: '', dci_code: '', dci_name: '', forme: '' }], notes: '' });
                              setOrdonnanceDialogOpen(true);
                            }}
                            className="bg-[#10B981] hover:bg-[#059669] text-white"
                          >
                            <Printer className="w-3 h-3 mr-1" />{t('doc_prescription_btn')}
                          </Button>
                        </div>
                      </div>

                      {/* Dossier médical */}
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-[#0F172A]">{t('doc_medical_record_title')}</h4>
                        {editingRecord ? (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setEditingRecord(false)}>{t('doc_cancel')}</Button>
                            <Button size="sm" onClick={handleSaveRecord} className="bg-[#0596DE] text-white">{t('doc_save')}</Button>
                          </div>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => setEditingRecord(true)}><Settings className="w-3 h-3 mr-1" />{t('doc_edit')}</Button>
                        )}
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Groupe sanguin */}
                        <div className="p-3 bg-[#F8FAFC] rounded-xl">
                          <p className="text-xs text-[#64748B] mb-1">{t('doc_blood_type_label')}</p>
                          {editingRecord ? (
                            <Select value={recordForm.blood_type} onValueChange={(v) => setRecordForm((p) => ({ ...p, blood_type: v }))}>
                              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder={t('doc_choose_placeholder')} /></SelectTrigger>
                              <SelectContent>{BLOOD_TYPES.map((bt) => <SelectItem key={bt} value={bt}>{bt}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (
                            <p className="font-semibold text-[#0F172A]">{patientDetail.medical_record?.blood_type || t('doc_not_specified')}</p>
                          )}
                        </div>

                        {/* Allergies */}
                        <div className="p-3 bg-[#FEE2E2] rounded-xl">
                          <p className="text-xs text-[#EF4444] mb-1">{t('doc_allergies_label')}</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {recordForm.allergies.map((a, i) => (
                              <span key={i} className="bg-[#EF4444] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                {a}
                                {editingRecord && <button onClick={() => setRecordForm((p) => ({ ...p, allergies: p.allergies.filter((_, j) => j !== i) }))}><XIcon className="w-2 h-2" /></button>}
                              </span>
                            ))}
                          </div>
                          {editingRecord && (
                            <div className="flex gap-1 mt-1">
                              <Input value={newAllergie} onChange={(e) => setNewAllergie(e.target.value)} placeholder={t('doc_add_placeholder')} className="h-7 text-xs" onKeyDown={(e) => { if (e.key === 'Enter' && newAllergie.trim()) { setRecordForm((p) => ({ ...p, allergies: [...p.allergies, newAllergie.trim()] })); setNewAllergie(''); }}} />
                              <Button size="sm" className="h-7 px-2" onClick={() => { if (newAllergie.trim()) { setRecordForm((p) => ({ ...p, allergies: [...p.allergies, newAllergie.trim()] })); setNewAllergie(''); }}}><Plus className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </div>

                        {/* Antécédents */}
                        <div className="p-3 bg-[#FEF3C7] rounded-xl">
                          <p className="text-xs text-[#F59E0B] mb-1">{t('doc_antecedents_label')}</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {recordForm.antecedents.map((a, i) => (
                              <span key={i} className="bg-[#F59E0B] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                {a}
                                {editingRecord && <button onClick={() => setRecordForm((p) => ({ ...p, antecedents: p.antecedents.filter((_, j) => j !== i) }))}><XIcon className="w-2 h-2" /></button>}
                              </span>
                            ))}
                          </div>
                          {editingRecord && (
                            <div className="flex gap-1 mt-1">
                              <Input value={newAntecedent} onChange={(e) => setNewAntecedent(e.target.value)} placeholder={t('doc_add_placeholder')} className="h-7 text-xs" onKeyDown={(e) => { if (e.key === 'Enter' && newAntecedent.trim()) { setRecordForm((p) => ({ ...p, antecedents: [...p.antecedents, newAntecedent.trim()] })); setNewAntecedent(''); }}} />
                              <Button size="sm" className="h-7 px-2" onClick={() => { if (newAntecedent.trim()) { setRecordForm((p) => ({ ...p, antecedents: [...p.antecedents, newAntecedent.trim()] })); setNewAntecedent(''); }}}><Plus className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </div>

                        {/* Maladies chroniques */}
                        <div className="p-3 bg-[#E6F4FB] rounded-xl">
                          <p className="text-xs text-[#0596DE] mb-1">{t('doc_chronic_conditions_label')}</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {recordForm.chronic_conditions.map((c, i) => (
                              <span key={i} className="bg-[#0596DE] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                {c}
                                {editingRecord && <button onClick={() => setRecordForm((p) => ({ ...p, chronic_conditions: p.chronic_conditions.filter((_, j) => j !== i) }))}><XIcon className="w-2 h-2" /></button>}
                              </span>
                            ))}
                          </div>
                          {editingRecord && (
                            <div className="flex gap-1 mt-1">
                              <Input value={newCondition} onChange={(e) => setNewCondition(e.target.value)} placeholder={t('doc_add_placeholder')} className="h-7 text-xs" onKeyDown={(e) => { if (e.key === 'Enter' && newCondition.trim()) { setRecordForm((p) => ({ ...p, chronic_conditions: [...p.chronic_conditions, newCondition.trim()] })); setNewCondition(''); }}} />
                              <Button size="sm" className="h-7 px-2" onClick={() => { if (newCondition.trim()) { setRecordForm((p) => ({ ...p, chronic_conditions: [...p.chronic_conditions, newCondition.trim()] })); setNewCondition(''); }}}><Plus className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Mesures */}
                    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                      <h4 className="font-semibold text-[#0F172A] mb-4">{t('doc_measurements_title')}</h4>
                      <div className="flex flex-wrap gap-2 mb-4 items-end">
                        <div className="w-48">
                          <Label className="text-xs text-[#64748B] mb-1 block">{t('doc_type_label')}</Label>
                          <Select
                            value={measurementForm.type}
                            onValueChange={(v) => {
                              const def = MEASUREMENT_INDEX[v];
                              setMeasurementForm((p) => ({ ...p, type: v, unit: def ? def.unit : p.unit, value: '', valueSystolic: '', valueDiastolic: '' }));
                            }}
                          >
                            <SelectTrigger className="rounded-xl h-9"><SelectValue placeholder={t('doc_choose_type_placeholder')} /></SelectTrigger>
                            <SelectContent>
                              {(SPECIALTY_SUGGESTIONS[profile?.specialty] || []).filter((v) => v !== 'imc').length > 0 && (
                                <SelectGroup>
                                  <SelectLabel>{t('doc_suggested_specialty')}</SelectLabel>
                                  {(SPECIALTY_SUGGESTIONS[profile?.specialty] || []).filter((v) => v !== 'imc').map((v) => {
                                    const def = MEASUREMENT_INDEX[v];
                                    return def ? <SelectItem key={`sugg-${v}`} value={v}>{def.label}</SelectItem> : null;
                                  })}
                                </SelectGroup>
                              )}
                              {Object.entries(MEASUREMENT_CATALOG).map(([catKey, cat]) => {
                                const suggested = new Set(SPECIALTY_SUGGESTIONS[profile?.specialty] || []);
                                // IMC n'est plus saisissable manuellement (Phase 3) : il est
                                // desormais calcule automatiquement a partir du poids/taille.
                                const remaining = cat.items.filter((item) => !suggested.has(item.value) && item.value !== 'imc');
                                if (remaining.length === 0) return null;
                                return (
                                  <SelectGroup key={catKey}>
                                    <SelectLabel>{cat.label}</SelectLabel>
                                    {remaining.map((item) => (
                                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                                    ))}
                                  </SelectGroup>
                                );
                              })}
                              <SelectItem value="__custom__">{t('doc_other_custom_measurement')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {measurementForm.type === '__custom__' && (
                          <div className="w-40">
                            <Label className="text-xs text-[#64748B] mb-1 block">{t('doc_measurement_name_label')}</Label>
                            <Input value={measurementForm.customType} onChange={(e) => setMeasurementForm((p) => ({ ...p, customType: e.target.value }))} placeholder={t('doc_ex_saturation_placeholder')} className="rounded-xl h-9" />
                          </div>
                        )}
                        {measurementForm.type === 'tension_arterielle' ? (
                          <>
                            <div className="w-20">
                              <Label className="text-xs text-[#64748B] mb-1 block">{t('doc_systolic_label')}</Label>
                              <Input type="number" value={measurementForm.valueSystolic} onChange={(e) => setMeasurementForm((p) => ({ ...p, valueSystolic: e.target.value }))} placeholder="120" className="rounded-xl h-9" />
                            </div>
                            <div className="w-20">
                              <Label className="text-xs text-[#64748B] mb-1 block">{t('doc_diastolic_label')}</Label>
                              <Input type="number" value={measurementForm.valueDiastolic} onChange={(e) => setMeasurementForm((p) => ({ ...p, valueDiastolic: e.target.value }))} placeholder="80" className="rounded-xl h-9" />
                            </div>
                          </>
                        ) : measurementForm.type === 'echelle_douleur' ? (
                          <div className="w-40">
                            <Label className="text-xs text-[#64748B] mb-1 block">{t('doc_pain_label')} {measurementForm.value || '0'}/10</Label>
                            <input
                              type="range"
                              min="0"
                              max="10"
                              step="1"
                              value={measurementForm.value || '0'}
                              onChange={(e) => setMeasurementForm((p) => ({ ...p, value: e.target.value }))}
                              className="w-full h-9 accent-[#0596DE]"
                            />
                          </div>
                        ) : (
                          <div className="w-28">
                            <Label className="text-xs text-[#64748B] mb-1 block">{t('doc_value_label')}</Label>
                            <Input value={measurementForm.value} onChange={(e) => setMeasurementForm((p) => ({ ...p, value: e.target.value }))} placeholder={`${t('doc_ex_prefix')} ${(MEASUREMENT_INDEX[measurementForm.type] || {}).placeholder || '14/9'}`} className="rounded-xl h-9" />
                          </div>
                        )}
                        <div className="w-24">
                          <Label className="text-xs text-[#64748B] mb-1 block">{t('doc_unit_label')}</Label>
                          <Input value={measurementForm.unit} onChange={(e) => setMeasurementForm((p) => ({ ...p, unit: e.target.value }))} placeholder={t('doc_ex_kg_placeholder')} className="rounded-xl h-9" />
                        </div>
                        <div className="w-40">
                          <Label className="text-xs text-[#64748B] mb-1 block">{t('doc_date_label')}</Label>
                          <Input type="date" value={measurementForm.date} onChange={(e) => setMeasurementForm((p) => ({ ...p, date: e.target.value }))} className="rounded-xl h-9" />
                        </div>
                        <Button onClick={handleAddMeasurement} className="bg-[#0596DE] text-white h-9"><Plus className="w-4 h-4 mr-1" />{t('doc_add_btn')}</Button>
                      </div>
                      {(() => {
                        const auto = getAutoBmi(patientDetail.medical_record?.measurements || []);
                        if (!auto) return null;
                        return (
                          <div className="mb-3 p-4 rounded-xl border border-[#BAE6FD] bg-[#F0F9FF]" style={{ borderLeftWidth: '4px', borderLeftColor: '#0596DE' }}>
                            <div className="flex items-center gap-2">
                              <span className="text-lg flex-shrink-0">⚖️</span>
                              <div>
                                <p className="text-sm font-semibold text-[#0F172A]">{t('doc_bmi_label')} {auto.bmi.toFixed(1)} kg/m² <span className="font-normal text-[#64748B]">{t('doc_calculated_auto')}</span></p>
                                <p className="text-xs text-[#94A3B8]">
                                  {t('doc_bmi_source_prefix')} {auto.latestPoids.value} kg ({format(parseISO(auto.latestPoids.date), 'dd MMM yyyy', { locale: fr })}) {t('doc_bmi_source_mid')} {auto.latestTaille.value} cm ({format(parseISO(auto.latestTaille.date), 'dd MMM yyyy', { locale: fr })})
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="space-y-3">
                        {(patientDetail.medical_record?.measurements || []).length === 0 ? (
                          <p className="text-sm text-[#64748B] text-center py-4">{t('doc_no_measurement')}</p>
                        ) : (
                          Object.entries(
                            (patientDetail.medical_record?.measurements || []).reduce((groups, m) => {
                              (groups[m.type] = groups[m.type] || []).push(m);
                              return groups;
                            }, {})
                          )
                            .sort((a, b) => {
                              const latestA = a[1].reduce((mx, m) => (m.date > mx ? m.date : mx), '');
                              const latestB = b[1].reduce((mx, m) => (m.date > mx ? m.date : mx), '');
                              return latestA < latestB ? 1 : latestA > latestB ? -1 : 0;
                            })
                            .map(([type, entries]) => {
                              const chrono = [...entries].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
                              const latest = chrono[chrono.length - 1];
                              const numericValues = chrono
                                .map((m) => parseFloat(String(m.value).replace(',', '.')))
                                .filter((v) => Number.isFinite(v));
                              const meta = getMeasurementTypeMeta(type);
                              return (
                                <div key={type} className="p-4 rounded-xl border border-[#E2E8F0] bg-white" style={{ borderLeftWidth: '4px', borderLeftColor: meta.color }}>
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-lg flex-shrink-0">{meta.icon}</span>
                                      <div className="min-w-0">
                                        <p className="text-sm font-semibold text-[#0F172A] truncate">{getMeasurementLabel(type)}</p>
                                        <p className="text-xs text-[#94A3B8]">
                                          {t('doc_last_visit_label')} {latest.value}{latest.unit ? ` ${latest.unit}` : ''} · {format(parseISO(latest.date), 'dd MMM yyyy', { locale: fr })}
                                        </p>
                                      </div>
                                    </div>
                                    {numericValues.length >= 2 && <MeasurementSparkline values={numericValues} color={meta.color} />}
                                  </div>
                                  <div className="mt-2 space-y-1">
                                    {chrono.slice().reverse().map((m) => (
                                      <div key={m.measurement_id} className="flex items-center justify-between gap-2 text-xs text-[#64748B] px-2 py-1 rounded-lg hover:bg-[#F8FAFC]">
                                        <span>{m.value}{m.unit ? ` ${m.unit}` : ''} · {format(parseISO(m.date), 'dd MMM yyyy', { locale: fr })}</span>
                                        <button onClick={() => handleDeleteMeasurement(m.measurement_id)} className="text-[#EF4444] hover:text-[#DC2626] flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                      <h4 className="font-semibold text-[#0F172A] mb-4">{t('doc_consultation_notes_title')}</h4>
                      <div className="flex gap-2 mb-4">
                        <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder={t('doc_add_note_placeholder')} rows={2} className="rounded-xl" />
                        <Button onClick={handleAddNote} className="bg-[#0596DE] text-white flex-shrink-0"><Plus className="w-4 h-4" /></Button>
                      </div>
                      <div className="space-y-2">
                        {(patientDetail.medical_record?.notes || []).length === 0 ? (
                          <p className="text-sm text-[#64748B] text-center py-4">{t('doc_no_note')}</p>
                        ) : (
                          [...(patientDetail.medical_record?.notes || [])].reverse().map((note) => (
                            <div key={note.note_id} className="p-3 bg-[#F8FAFC] rounded-xl flex justify-between gap-2">
                              <div>
                                <p className="text-sm text-[#0F172A]">{note.content}</p>
                                <p className="text-xs text-[#94A3B8] mt-1">{format(parseISO(note.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}</p>
                              </div>
                              <button onClick={() => handleDeleteNote(note.note_id)} className="text-[#EF4444] hover:text-[#DC2626] flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Historique RDV */}
                    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                      <h4 className="font-semibold text-[#0F172A] mb-4">{t('doc_visit_history_title')}</h4>
                      <div className="space-y-2">
                        {(patientDetail.appointments || []).map((apt) => {
                          // Correctif : s'appuyer sur le vrai statut "no_show" (passe
                          // automatiquement 2h apres l'heure du RDV si aucune presence n'a ete signalee, cote backend) plutot
                          // que sur une heuristique de date.
                          const aptNoShow = apt.status === 'no_show';
                          const badgeClass = apt.status === 'completed' ? 'bg-[#D1FAE5] text-[#10B981]'
                            : apt.status === 'cancelled' ? 'bg-[#FEE2E2] text-[#EF4444]'
                            : aptNoShow ? 'bg-[#F1F5F9] text-[#475569]'
                            : apt.status === 'confirmed' ? 'bg-[#D1FAE5] text-[#10B981]'
                            : 'bg-[#FEF3C7] text-[#F59E0B]';
                          const badgeLabel = apt.status === 'completed' ? t('doc_badge_completed')
                            : apt.status === 'cancelled' ? t('doc_badge_cancelled')
                            : aptNoShow ? t('doc_no_show_badge')
                            : apt.status === 'confirmed' ? t('doc_badge_confirmed')
                            : t('doc_badge_pending');
                          return (
                          <div key={apt.appointment_id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl">
                            <div>
                              <p className="text-sm font-medium text-[#0F172A]">{apt.date} à {apt.time}</p>
                              {apt.reason && <p className="text-xs text-[#64748B]">📋 {apt.reason}</p>}
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>{badgeLabel}</span>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </TabsContent>

          {/* TAB 4 : STATISTIQUES */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              <RevenueChart appointments={appointments} profile={profile} dateFnsLocale={dateFnsLocale} />
              <ConsultationStats appointments={appointments} profile={profile} t={t} />
            </div>
          </TabsContent>

          {/* TAB 5 : HORAIRES */}
          <TabsContent value="schedule">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#0F172A]">{t('doc_availability')}</h3>
                {editingSchedule ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setEditSchedule(profile.schedule || {}); setEditingSchedule(false); }}>{t('doc_cancel')}</Button>
                    <Button onClick={handleScheduleUpdate} disabled={actionLoading} className="bg-[#0596DE]" data-testid="save-schedule-btn">{actionLoading ? t('doc_saving') : t('doc_save')}</Button>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => setEditingSchedule(true)} data-testid="edit-schedule-btn"><Settings className="w-4 h-4 mr-2" />{t('doc_edit')}</Button>
                )}
              </div>
              {editingSchedule && (
                <div className="mb-6 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                  <Label className="mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-[#0596DE]" />{t('doc_consultation_duration_label')}</Label>
                  <Select value={consultationDuration} onValueChange={setConsultationDuration}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">{t('doc_15min')}</SelectItem>
                      <SelectItem value="30">{t('doc_30min')}</SelectItem>
                      <SelectItem value="45">{t('doc_45min')}</SelectItem>
                      <SelectItem value="60">{t('doc_1hour')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Correctif honnetete UX : ce champ ne regenere PAS la grille de
                      creneaux ci-dessous (fixe a 30 min, voir TIME_SLOTS) — il sert
                      seulement au calcul de la duree de la salle video et de l'heure
                      de fin estimee cote patient. Sans ce texte, le medecin peut
                      croire (a raison, l'intitule pretait a confusion) que changer
                      cette valeur va faire apparaitre des creneaux plus fins. */}
                  <p className="text-xs text-[#64748B] mt-2">{t('doc_consultation_duration_hint')}</p>
                </div>
              )}
              <div className="space-y-6">
                {DAYS.map((day) => (
                  <div key={day} className="border-b border-[#E2E8F0] pb-4 last:border-0">
                    <h4 className="font-medium text-[#0F172A] mb-3 flex items-center gap-2">{DAY_NAMES_MAP[day]}<span className="text-xs text-[#64748B]">({(editSchedule[day] || []).length} {t('doc_slots_word')})</span></h4>
                    {/* Correctif (demande Nadjib, 20/09/2026) : quand la
                        teleconsultation est activee, chaque creneau coche
                        affiche desormais un petit choix Cabinet / Video /
                        Les deux - avant, "video" etait propose au patient
                        pour N'IMPORTE QUEL creneau ouvert des que la
                        teleconsultation etait activee QUELQUE PART chez ce
                        medecin, meme un creneau ou il n'est pas
                        physiquement au cabinet. Rien ne change visuellement
                        pour un medecin qui ne propose pas la video. */}
                    <div className="flex flex-wrap gap-2 items-start">
                      {TIME_SLOTS.map((slot) => {
                        const selectedEntry = (editSchedule[day] || []).find((s) => slotEntryTime(s) === slot);
                        const isSelected = !!selectedEntry;
                        const currentTypes = isSelected ? slotEntryTypes(selectedEntry, profile.video_consultation_enabled) : [];
                        const hasInPerson = currentTypes.includes('in_person');
                        const hasVideo = currentTypes.includes('video');
                        return (
                          <div key={slot} className="flex flex-col items-center gap-1">
                            <button onClick={() => editingSchedule && toggleSlot(day, slot)} disabled={!editingSchedule}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${isSelected ? 'bg-[#0596DE] text-white shadow-md scale-105' : 'bg-[#F1F5F9] text-[#64748B]'} ${editingSchedule ? 'cursor-pointer hover:scale-105 hover:shadow-md hover:bg-[#E2E8F0]' : 'cursor-default opacity-70'}`}>
                              {slot}
                            </button>
                            {isSelected && editingSchedule && profile.video_consultation_enabled && (
                              <div className="flex gap-0.5">
                                <button type="button" onClick={() => setSlotTypes(day, slot, ['in_person'])}
                                  title={t('doc_slot_type_in_person')}
                                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight ${hasInPerson && !hasVideo ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>
                                  {t('doc_slot_type_in_person_short')}
                                </button>
                                <button type="button" onClick={() => setSlotTypes(day, slot, ['video'])}
                                  title={t('doc_slot_type_video')}
                                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight ${hasVideo && !hasInPerson ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>
                                  {t('doc_slot_type_video_short')}
                                </button>
                                <button type="button" onClick={() => setSlotTypes(day, slot, ['in_person', 'video'])}
                                  title={t('doc_slot_type_both')}
                                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight ${hasInPerson && hasVideo ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>
                                  {t('doc_slot_type_both_short')}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* TAB 6 : PROFIL */}
          <TabsContent value="profile">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#0F172A]">{t('doc_my_profile')}</h3>
                {editingProfile ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setEditingProfile(false); setProfileForm({ phone: profile.phone || '', address: profile.address || '', fees: profile.fees?.toString() || '', bio: profile.bio || '', education: profile.education || '', photo_url: profile.photo_url || '', languages: profile.languages || [], accepted_insurance: profile.accepted_insurance || [], video_consultation_enabled: profile.video_consultation_enabled || false, video_payment_ccp: profile.video_payment_ccp || '' }); }}>{t('doc_cancel')}</Button>
                    <Button onClick={handleProfileUpdate} disabled={actionLoading} className="bg-[#0596DE]" data-testid="save-profile-btn"><Save className="w-4 h-4 mr-2" />{actionLoading ? t('doc_saving') : t('doc_save')}</Button>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => setEditingProfile(true)} data-testid="edit-profile-btn"><Settings className="w-4 h-4 mr-2" />{t('doc_edit')}</Button>
                )}
              </div>
              <div className="flex items-start gap-6 mb-8 p-4 bg-[#F8FAFC] rounded-xl">
                {profile.photo_url ? <img src={profile.photo_url} alt={profile.name} className="w-24 h-24 rounded-xl object-cover" /> : <div className="w-24 h-24 rounded-xl bg-[#E6F4FB] flex items-center justify-center"><span className="text-3xl font-bold text-[#0596DE]">{profile.name?.split(' ').map((n) => n[0]).join('').slice(0, 2)}</span></div>}
                <div>
                  <h2 className="text-xl font-bold text-[#0F172A]">{profile.name}</h2>
                  <p className="text-[#0596DE] font-medium">{SPECIALTIES_MAP[profile.specialty] || profile.specialty}</p>
                  <p className="text-sm text-[#64748B]">{t('doc_license')}: {profile.license_number}</p>
                  {profile.average_rating > 0 && <p className="text-sm text-[#64748B] mt-1">{profile.average_rating}/5 ({profile.review_count} {t('doc_rating')})</p>}
                </div>
              </div>
              {editingProfile ? (
                <div className="space-y-6">
                  <div>
                    <Label className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-[#0596DE]" />{t('doc_photo_url')}</Label>
                    <ImageUploader
                      value={profileForm.photo_url}
                      onChange={(url) => setProfileForm((p) => ({ ...p, photo_url: url || '' }))}
                      label={t('doc_photo_url')}
                      aspect="square"
                      className="w-32"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><Label className="flex items-center gap-2 mb-2"><Phone className="w-4 h-4 text-[#0596DE]" />{t('doc_phone')}</Label><Input value={profileForm.phone} onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))} className="rounded-xl" data-testid="edit-phone" /></div>
                    <div><Label className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-[#0596DE]" />{t('doc_fees')}</Label><Input type="number" value={profileForm.fees} onChange={(e) => setProfileForm((p) => ({ ...p, fees: e.target.value }))} className="rounded-xl" data-testid="edit-fees" /></div>
                  </div>
                  <div><Label className="flex items-center gap-2 mb-2"><MapPin className="w-4 h-4 text-[#0596DE]" />{t('doc_address')}</Label><Input value={profileForm.address} onChange={(e) => setProfileForm((p) => ({ ...p, address: e.target.value }))} className="rounded-xl" data-testid="edit-address" /></div>
                  <div><Label className="flex items-center gap-2 mb-2"><GraduationCap className="w-4 h-4 text-[#0596DE]" />{t('doc_education')}</Label><Input value={profileForm.education} onChange={(e) => setProfileForm((p) => ({ ...p, education: e.target.value }))} className="rounded-xl" data-testid="edit-education" /></div>
                  <div><Label className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-[#0596DE]" />{t('doc_bio')}</Label><Textarea value={profileForm.bio} onChange={(e) => setProfileForm((p) => ({ ...p, bio: e.target.value }))} rows={4} data-testid="edit-bio" /></div>
                  <div>
                    <Label className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-[#0596DE]" />{t('doc_languages')}</Label>
                    <div className="flex flex-wrap gap-2 mb-2">{profileForm.languages.map((language) => <Badge key={language} variant="secondary" className="px-3 py-1 bg-[#F1F5F9] text-[#475569] cursor-pointer hover:bg-red-50 hover:text-red-500" onClick={() => removeLanguage(language)}>{language} <XIcon className="w-3 h-3 ml-1" /></Badge>)}</div>
                    <Select value={newLanguage} onValueChange={addLanguage}><SelectTrigger className="rounded-xl" data-testid="add-language-select"><SelectValue placeholder={t('doc_add_language')} /></SelectTrigger><SelectContent>{LANGUAGE_OPTIONS.filter((l) => !profileForm.languages.includes(l)).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-[#0596DE]" />{t('doc_insurance')}</Label>
                    <div className="flex flex-wrap gap-2 mb-2">{profileForm.accepted_insurance.map((ins) => <Badge key={ins} variant="secondary" className="px-3 py-1 bg-[#F1F5F9] text-[#475569] cursor-pointer hover:bg-red-50 hover:text-red-500" onClick={() => removeInsurance(ins)}>{ins} <XIcon className="w-3 h-3 ml-1" /></Badge>)}</div>
                    <Select value={newInsurance} onValueChange={addInsurance}><SelectTrigger className="rounded-xl" data-testid="add-insurance-select"><SelectValue placeholder={t('doc_add_insurance')} /></SelectTrigger><SelectContent>{INSURANCE_OPTIONS.filter((i) => !profileForm.accepted_insurance.includes(i)).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select>
                  </div>
                  {/* ✅ NOUVEAU (teleconsultation) : le medecin choisit lui-meme
                      de proposer la video a ses patients, RDV par RDV cote
                      DoctorProfile.js (le choix "video" n'apparait que si ce
                      reglage est actif). */}
                  <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-xl">
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4 text-[#0596DE]" />
                      <div>
                        <Label className="mb-0">{t('doc_video_consultation_label')}</Label>
                        <p className="text-xs text-[#64748B]">{t('doc_video_consultation_desc')}</p>
                      </div>
                    </div>
                    <Switch checked={profileForm.video_consultation_enabled} onCheckedChange={(checked) => setProfileForm((p) => ({ ...p, video_consultation_enabled: checked }))} data-testid="edit-video-consultation-switch" />
                  </div>
                  {/* ✅ NOUVEAU (teleconsultation, paiement manuel BaridiMob/CCP) :
                      affiche au patient dans le dialog de reservation
                      (DoctorProfile.js) pour qu'il sache ou virer le montant
                      avant que le medecin ne confirme le RDV. */}
                  {profileForm.video_consultation_enabled && (
                    <div>
                      <Label className="flex items-center gap-2 mb-2">{t('doc_video_payment_ccp_label')}</Label>
                      <Input value={profileForm.video_payment_ccp} onChange={(e) => setProfileForm((p) => ({ ...p, video_payment_ccp: e.target.value }))} placeholder={t('doc_video_payment_ccp_placeholder')} className="rounded-xl" data-testid="edit-video-payment-ccp" />
                      <p className="text-xs text-[#64748B] mt-1">{t('doc_video_payment_ccp_desc')}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4 text-sm text-[#475569]">
                  {profile.bio && <div className="p-4 bg-[#F8FAFC] rounded-xl"><p className="font-medium text-[#0F172A] mb-1">{t('doc_bio')}</p><p>{profile.bio}</p></div>}
                  {profile.education && <div className="p-4 bg-[#F8FAFC] rounded-xl"><p className="font-medium text-[#0F172A] mb-1">{t('doc_education')}</p><p>{profile.education}</p></div>}
                  {profile.languages?.length > 0 && <div className="p-4 bg-[#F8FAFC] rounded-xl"><p className="font-medium text-[#0F172A] mb-2">{t('doc_languages')}</p><div className="flex flex-wrap gap-2">{profile.languages.map((l) => <Badge key={l} variant="secondary">{l}</Badge>)}</div></div>}
                  {profile.accepted_insurance?.length > 0 && <div className="p-4 bg-[#F8FAFC] rounded-xl"><p className="font-medium text-[#0F172A] mb-2">{t('doc_insurance')}</p><div className="flex flex-wrap gap-2">{profile.accepted_insurance.map((i) => <Badge key={i} variant="secondary">{i}</Badge>)}</div></div>}
                  <div className="p-4 bg-[#F8FAFC] rounded-xl flex items-center gap-2">
                    <Video className="w-4 h-4 text-[#0596DE]" />
                    <p>{profile.video_consultation_enabled ? t('doc_video_consultation_active') : t('doc_video_consultation_inactive')}</p>
                  </div>
                  {profile.video_consultation_enabled && !profile.video_payment_ccp && (
                    <div className="p-4 bg-[#FEF3C7] rounded-xl text-[#92400E]">{t('doc_video_payment_ccp_none')}</div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB MESSAGES */}
          <TabsContent value="messages">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <button onClick={() => setMsgTab('inbox')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${msgTab === 'inbox' ? 'bg-[#0596DE] text-white' : 'bg-white border border-[#E2E8F0] text-[#475569]'}`}>
                    <Inbox className="w-4 h-4 inline mr-1" />
                    {t('doc_inbox_tab')} {msgUnreadCount > 0 && <span className="bg-[#EF4444] text-white text-xs px-1.5 py-0.5 rounded-full ml-1">{msgUnreadCount}</span>}
                  </button>
                  <button onClick={() => setMsgTab('sent')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${msgTab === 'sent' ? 'bg-[#0596DE] text-white' : 'bg-white border border-[#E2E8F0] text-[#475569]'}`}>
                    <Send className="w-4 h-4 inline mr-1" />
                    {t('doc_sent_tab')}
                  </button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-full" onClick={fetchMessages}><RefreshIcon className="w-4 h-4" /></Button>
                  <Button className="rounded-full bg-[#0596DE]" onClick={() => setShowCompose(!showCompose)}>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t('doc_new_message_btn')}
                  </Button>
                </div>
              </div>

              {/* Formulaire de composition */}
              {showCompose && (
                <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-4">
                  <h3 className="font-semibold text-[#0F172A] text-lg">{t('doc_new_medical_message_title')}</h3>

                  {/* Modèle */}
                  <div>
                    <label className="text-sm font-medium text-[#475569] mb-1 block">{t('doc_use_template_label')}</label>
                    <select value={selectedTemplate} onChange={(e) => handleSelectTemplate(e.target.value)} className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] bg-white">
                      <option value="">{t('doc_choose_template_option')}</option>
                      {['Références', 'Résultats d\'analyse', 'Résultats imagerie', 'Avis médical', 'Suivi patient', 'Urgences', 'Général'].map(cat => (
                        <optgroup key={cat} label={t(CATEGORY_LABEL_KEYS[cat] || cat)}>
                          {templates.filter(t => t.category === cat).map(t => (
                            <option key={t.id} value={t.id}>{t.icon} {t.title}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {/* Destinataire */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#475569] mb-1 block">{t('doc_doctor_on_platform_label')}</label>
                      <div className="relative">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                        <input
                          type="text"
                          value={doctorSearch}
                          onChange={(e) => {
  setDoctorSearch(e.target.value);
  searchDoctors(e.target.value);
  if (!e.target.value) setMsgForm(prev => ({ ...prev, recipient_doctor_id: '', recipient_name: '' }));
}}
                          placeholder={t('doc_search_dr_placeholder')}
                          className="w-full border border-[#E2E8F0] rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                        />
                        {doctorResults.length > 0 && (
                          <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-xl shadow-lg">
                            {doctorResults.map(d => (
                              <button key={d.doctor_id} onClick={() => { setMsgForm(prev => ({ ...prev, recipient_doctor_id: d.doctor_id, recipient_name: d.name, recipient_email: '' })); setDoctorSearch(d.name); setDoctorResults([]); }}
                                className="w-full text-left px-4 py-2 hover:bg-[#F8FAFC] text-sm">
                                <span className="font-medium">{d.name}</span>
                                <span className="text-[#64748B] ml-2">{d.specialty}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#475569] mb-1 block">{t('doc_external_email_label')}</label>
                      <input type="email" value={msgForm.recipient_email} onChange={(e) => setMsgForm(prev => ({ ...prev, recipient_email: e.target.value, recipient_doctor_id: '', recipient_name: prev.recipient_name }))}
                        placeholder="docteur@exemple.dz"
                        className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                        disabled={!!msgForm.recipient_doctor_id}
                      />
                    </div>
                  </div>

                  {/* Patient + Priorité */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#475569] mb-1 block">{t('doc_patient_name_label')}</label>
                      <input type="text" value={msgForm.patient_name} onChange={(e) => setMsgForm(prev => ({ ...prev, patient_name: e.target.value }))}
                        placeholder={t('doc_optional_placeholder')}
                        className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#475569] mb-1 block">{t('doc_age_label')}</label>
                      <input type="number" value={msgForm.patient_age} onChange={(e) => setMsgForm(prev => ({ ...prev, patient_age: e.target.value }))}
                        placeholder={t('doc_optional_placeholder')}
                        className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#475569] mb-1 block">{t('doc_priority_label')}</label>
                      <select value={msgForm.priority} onChange={(e) => setMsgForm(prev => ({ ...prev, priority: e.target.value }))}
                        className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] bg-white">
                        <option value="normal">{t('doc_priority_normal')}</option>
                        <option value="urgent">{t('doc_priority_urgent')}</option>
                      </select>
                    </div>
                  </div>

                  {/* Sujet */}
                  <div>
                    <label className="text-sm font-medium text-[#475569] mb-1 block">{t('doc_subject_label')}</label>
                    <input type="text" value={msgForm.subject} onChange={(e) => setMsgForm(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder={t('doc_subject_placeholder')}
                      className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                    />
                  </div>

                  {/* Corps du message */}
                  <div>
                    <label className="text-sm font-medium text-[#475569] mb-1 block">{t('doc_message_label')}</label>
                    <textarea value={msgForm.body} onChange={(e) => setMsgForm(prev => ({ ...prev, body: e.target.value }))}
                      rows={10}
                      className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] resize-none font-mono"
                      placeholder={t('doc_write_message_placeholder')}
                    />
                  </div>

                  {/* Boutons */}
                  {/* Pièce jointe */}
                  <div className="border border-dashed border-[#E2E8F0] rounded-xl p-4">
                    <label className="cursor-pointer flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#E6F4FB] flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-[#0596DE]" />
                      </div>
                      <div className="flex-1">
                        {msgForm.attachment_name ? (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[#0596DE] font-medium">📎 {msgForm.attachment_name}</span>
                            <button onClick={() => setMsgForm(prev => ({ ...prev, attachment_name: '', attachment_data: '' }))}
                              className="text-red-400 hover:text-red-600 text-xs ml-2">{t('doc_delete_btn')}</button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm font-medium text-[#0F172A]">{t('doc_add_attachment')}</p>
                            <p className="text-xs text-[#64748B]">{t('doc_attachment_hint')}</p>
                          </div>
                        )}
                      </div>
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} />
                    </label>
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button variant="outline" className="rounded-full" onClick={() => { setShowCompose(false); setSelectedTemplate(''); }}>{t('doc_cancel')}</Button>
                    <Button className="rounded-full bg-[#0596DE]" onClick={handleSendMessage} disabled={msgLoading}>
                      <Send className="w-4 h-4 mr-2" />
                      {msgLoading ? t('doc_sending') : t('doc_send_btn')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Liste messages */}
              <div className="space-y-3">
                {(msgTab === 'inbox' ? messages : sentMessages).length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl border border-[#E2E8F0]">
                    <MessageSquare className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                    <p className="text-[#64748B]">{msgTab === 'inbox' ? t('doc_no_message_received') : t('doc_no_message_sent')}</p>
                  </div>
                ) : (
                  (msgTab === 'inbox' ? messages : sentMessages).map(msg => (
                    <div key={msg.message_id} onClick={() => { setSelectedMsg(msg); if (msgTab === 'inbox' && !msg.read) handleMarkMsgRead(msg.message_id); }}
                      className={`bg-white rounded-2xl border p-5 cursor-pointer hover:shadow-md transition-all ${!msg.read && msgTab === 'inbox' ? 'border-[#0596DE] bg-[#F0F9FF]' : 'border-[#E2E8F0]'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {msg.priority === 'urgent' && <span className="text-xs bg-[#EF4444] text-white px-2 py-0.5 rounded-full">{t('doc_urgent_badge')}</span>}
                            {!msg.read && msgTab === 'inbox' && <span className="w-2 h-2 rounded-full bg-[#0596DE] inline-block" />}
                            <span className="font-semibold text-[#0F172A] text-sm truncate">{msg.subject}</span>
                          </div>
                          <p className="text-xs text-[#64748B]">
                            {msgTab === 'inbox' ? `${t('doc_from_label')} Dr. ${msg.sender_name}` : `${t('doc_to_label')} ${msg.recipient_name}`}
                            {msg.patient_name && ` • ${t('doc_patient_label')} ${msg.patient_name}${msg.patient_age ? ` (${msg.patient_age} ${t('doc_years_word')})` : ''}`}
                          </p>
                          <p className="text-sm text-[#475569] mt-2 line-clamp-2 whitespace-pre-line">{msg.body.substring(0, 150)}...</p>
                          {msg.attachment_name && <p className="text-xs text-[#0596DE] mt-1">📎 {msg.attachment_name}</p>}
                        </div>
                        <span className="text-xs text-[#94A3B8] whitespace-nowrap">
                          {new Date(msg.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        <button onClick={(e) => handleDeleteMsg(msg.message_id, e)} className="text-red-400 hover:text-red-600 mt-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

              {/* Modal lecture message */}
              {selectedMsg && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedMsg(null)}>
                  <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <div className="p-6 border-b border-[#E2E8F0]">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {selectedMsg.priority === 'urgent' && <span className="text-xs bg-[#EF4444] text-white px-2 py-0.5 rounded-full">{t('doc_urgent_badge')}</span>}
                            <h3 className="font-bold text-[#0F172A] text-lg">{selectedMsg.subject}</h3>
                          </div>
                          <p className="text-sm text-[#64748B]">{msgTab === 'inbox' ? `${t('doc_from_label')} Dr. ${selectedMsg.sender_name} (${selectedMsg.sender_specialty})` : `${t('doc_to_label')} ${selectedMsg.recipient_name}`}</p>
                          <p className="text-xs text-[#94A3B8] mt-1">{new Date(selectedMsg.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                          {selectedMsg.patient_name && <p className="text-sm mt-2 bg-[#FEF3C7] px-3 py-1 rounded-full inline-block">{t('doc_patient_emoji_label')} {selectedMsg.patient_name}{selectedMsg.patient_age ? ` (${selectedMsg.patient_age} ${t('doc_years_word')})` : ''}</p>}
                        </div>
                        <button onClick={() => setSelectedMsg(null)} className="text-[#94A3B8] hover:text-[#475569] text-2xl leading-none">×</button>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="bg-[#F8FAFC] rounded-xl p-4 whitespace-pre-line text-[#475569] leading-relaxed text-sm">{selectedMsg.body}</div>
                      {selectedMsg.attachment_name && (
                        <div className="mt-4 p-3 bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl">
                          <p className="text-sm text-[#0596DE]">📎 {selectedMsg.attachment_name}</p>
                        </div>
                      )}
                    </div>
                    {msgTab === 'inbox' && (
                      <div className="p-6 border-t border-[#E2E8F0] flex justify-end gap-3">
                        <button onClick={() => setSelectedMsg(null)} className="px-4 py-2 rounded-full border border-[#E2E8F0] text-sm text-[#475569] hover:bg-[#F8FAFC]">{t('doc_close_btn')}</button>
                        <button onClick={() => handleReply(selectedMsg)} className="px-4 py-2 rounded-full bg-[#0596DE] text-white text-sm hover:bg-[#047CBA]">{t('doc_reply_btn')}</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </TabsContent>

          {/* TAB 7 : ABONNEMENT */}
          <TabsContent value="subscription">
            <SubscriptionTab subscription={subscription} onPayClick={handlePayClick} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog statut RDV */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('doc_status_title')}</DialogTitle>
            <DialogDescription>{t('doc_status_desc')} {selectedAppointment?.patient?.name}</DialogDescription>
          </DialogHeader>
          {(() => {
            if (!selectedAppointment) return null;
            // Correctif : s'appuyer sur le vrai statut "no_show" plutot que sur une
            // heuristique de date.
            const patientNoShowCount = appointments.filter(
              (a) => a.patient_id === selectedAppointment.patient_id && a.status === 'no_show'
            ).length;
            return (
              <>
                {patientNoShowCount > 0 && (
                  <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 text-sm text-[#B91C1C]">
                    {t('doc_patient_already_prefix')} {patientNoShowCount} {patientNoShowCount > 1 ? t('doc_no_show_appointments_plural') : t('doc_no_show_appointment_singular')} {t('doc_absence_no_cancel_suffix')}
                  </div>
                )}
                {/* ✅ NOUVEAU : le medecin peut desormais marquer lui-meme un
                    RDV "non honore" (avant, seul le job automatique 2h apres
                    l'heure prevue le faisait). S'il choisit ce statut alors
                    que le patient s'est bien connecte a la salle video, on
                    l'avertit clairement avant qu'il valide — pour eviter une
                    erreur (ex : consultation reellement tenue mais medecin
                    qui se trompe de statut). */}
                {newStatus === 'no_show' && selectedAppointment.consultation_type === 'video' && selectedAppointment.arrived_at && (
                  <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3 text-sm text-[#B45309] mt-2">
                    ⚠️ {t('doc_no_show_video_connected_warning')}
                  </div>
                )}
              </>
            );
          })()}
          <div className="py-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed">{t('doc_confirmed')}</SelectItem>
                <SelectItem value="completed">{t('doc_completed')}</SelectItem>
                <SelectItem value="cancelled">{t('doc_cancelled')}</SelectItem>
                <SelectItem value="no_show">{t('doc_no_show_badge')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>{t('doc_cancel')}</Button>
            <Button onClick={handleStatusChange} disabled={actionLoading} className="bg-[#0596DE]" data-testid="confirm-status-btn">{actionLoading ? t('doc_saving') : t('patient_confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog déplacement RDV */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-[#F59E0B]" />{t('doc_reschedule_dialog_title')}</DialogTitle>
            <DialogDescription>{t('doc_dialog_patient_label')} <strong>{rescheduleAppointment?.patient?.name}</strong><br />{t('doc_currently_on')} <strong>{rescheduleAppointment?.date}</strong> {t('doc_at_time')} <strong>{rescheduleAppointment?.time}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label className="mb-2 block">{t('doc_new_date_label')}</Label><Input type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="rounded-xl" /></div>
            <div>
              <Label className="mb-2 block">{t('doc_new_time_label')}</Label>
              <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder={t('doc_choose_time_placeholder')} /></SelectTrigger>
                <SelectContent>
                  {getAvailableSlotsForDate(rescheduleDate, rescheduleAppointment?.appointment_id).map((slot) => <SelectItem key={slot} value={slot}>{slot}</SelectItem>)}
                </SelectContent>
              </Select>
              {rescheduleDate && getAvailableSlotsForDate(rescheduleDate, rescheduleAppointment?.appointment_id).length === 0 && (
                <p className="text-xs text-[#94A3B8] mt-1">{t('doc_no_slot_available')}</p>
              )}
            </div>
            <div><Label className="mb-2 block">{t('doc_reschedule_reason_label')}</Label><Textarea value={rescheduleReason} onChange={(e) => setRescheduleReason(e.target.value)} placeholder={t('doc_reschedule_reason_placeholder')} rows={3} className="rounded-xl" /></div>
            <div className="p-3 bg-[#FEF3C7] rounded-xl text-sm text-[#92400E]">{t('doc_email_notification_notice')}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>{t('doc_cancel')}</Button>
            <Button onClick={handleRescheduleSubmit} disabled={actionLoading || !rescheduleDate || !rescheduleTime} className="bg-[#F59E0B] hover:bg-[#D97706] text-white">{actionLoading ? t('doc_moving') : t('doc_confirm_reschedule_btn')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✅ NOUVEAU : Dialog ajouter un patient hors plateforme */}
      <Dialog open={manualPatientDialogOpen} onOpenChange={setManualPatientDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-[#0596DE]" />{t('doc_manual_patient_title')}</DialogTitle>
            <DialogDescription>{t('doc_manual_patient_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label className="mb-2 block">{t('doc_full_name_label')}</Label><Input value={manualPatientForm.name} onChange={(e) => setManualPatientForm((p) => ({ ...p, name: e.target.value }))} placeholder={t('doc_ex_name_placeholder')} className="rounded-xl" /></div>
            <div><Label className="mb-2 block">{t('doc_phone')}</Label><Input value={manualPatientForm.phone} onChange={(e) => setManualPatientForm((p) => ({ ...p, phone: e.target.value }))} placeholder={t('doc_ex_phone_placeholder')} className="rounded-xl" /></div>
            <div><Label className="mb-2 block">{t('doc_email_label_full')}</Label><Input type="email" value={manualPatientForm.email} onChange={(e) => setManualPatientForm((p) => ({ ...p, email: e.target.value }))} placeholder={t('doc_ex_email_placeholder')} className="rounded-xl" /></div>
            <div><Label className="mb-2 block">{t('doc_dob_label')}</Label><Input type="date" value={manualPatientForm.date_of_birth} onChange={(e) => setManualPatientForm((p) => ({ ...p, date_of_birth: e.target.value }))} className="rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualPatientDialogOpen(false)}>{t('doc_cancel')}</Button>
            <Button onClick={handleAddManualPatient} disabled={manualPatientLoading} className="bg-[#0596DE] hover:bg-[#047CBA] text-white">{manualPatientLoading ? t('doc_adding') : t('doc_add_patient_btn')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✅ NOUVEAU : Dialog programmer un RDV */}
      <Dialog open={bookDialogOpen} onOpenChange={setBookDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0596DE]" />
              {t('doc_book_appointment_title')}
            </DialogTitle>
            <DialogDescription>
              {t('doc_dialog_patient_label')} <strong>{patientDetail?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">{t('doc_date_label')}</Label>
              <Input
                type="date"
                value={bookForm.date}
                onChange={(e) => setBookForm((p) => ({ ...p, date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label className="mb-2 block">{t('doc_time_label')}</Label>
              <Select value={bookForm.time} onValueChange={(v) => setBookForm((p) => ({ ...p, time: v }))}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder={t('doc_choose_time_placeholder')} /></SelectTrigger>
                <SelectContent>
                  {getAvailableSlotsForDate(bookForm.date).map((slot) => <SelectItem key={slot} value={slot}>{slot}</SelectItem>)}
                </SelectContent>
              </Select>
              {bookForm.date && getAvailableSlotsForDate(bookForm.date).length === 0 && (
                <p className="text-xs text-[#94A3B8] mt-1">{t('doc_no_slot_available')}</p>
              )}
            </div>
            {/* ✅ NOUVEAU : le medecin peut aussi programmer une teleconsultation
                lui-meme (pas seulement le patient depuis sa fiche). Visible
                uniquement si la teleconsultation est activee sur le profil —
                meme regle que cote patient (DoctorProfile.js). */}
            {profile?.video_consultation_enabled && (
              <div>
                <Label className="mb-2 block">{t('doc_book_consultation_type_label')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBookForm((p) => ({ ...p, consultationType: 'in_person' }))}
                    className={`rounded-lg border px-3 py-2 text-sm text-left ${bookForm.consultationType === 'in_person' ? 'border-[#0596DE] bg-[#F0F9FF] text-[#0596DE] font-medium' : 'border-[#E2E8F0] text-[#64748B]'}`}
                  >
                    {t('doc_book_type_in_person')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookForm((p) => ({ ...p, consultationType: 'video' }))}
                    className={`rounded-lg border px-3 py-2 text-sm text-left ${bookForm.consultationType === 'video' ? 'border-[#0596DE] bg-[#F0F9FF] text-[#0596DE] font-medium' : 'border-[#E2E8F0] text-[#64748B]'}`}
                  >
                    {t('doc_book_type_video')}
                  </button>
                </div>
                {bookForm.consultationType === 'video' && (
                  <p className="text-xs text-[#0596DE] mt-2">{t('doc_book_video_notice')}</p>
                )}
              </div>
            )}
            <div>
              <Label className="mb-2 block">{t('doc_reason_optional_label')}</Label>
              <Textarea
                value={bookForm.reason}
                onChange={(e) => setBookForm((p) => ({ ...p, reason: e.target.value }))}
                placeholder={t('doc_book_reason_placeholder')}
                rows={3}
                className="rounded-xl"
              />
            </div>
            <div className="p-3 bg-[#E6F4FB] rounded-xl text-sm text-[#0596DE]">
              {t('doc_confirmation_email_notice')}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookDialogOpen(false)}>{t('doc_cancel')}</Button>
            <Button
              onClick={handleBookAppointment}
              disabled={bookLoading || !bookForm.date || !bookForm.time}
              className="bg-[#0596DE] hover:bg-[#047CBA] text-white"
            >
              {bookLoading ? t('doc_booking_in_progress') : t('doc_confirm_appointment_btn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✅ NOUVEAU : Dialog supprimer patient */}
      <Dialog open={deletePatientDialogOpen} onOpenChange={setDeletePatientDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {t('doc_delete_patient_title')}
            </DialogTitle>
            <DialogDescription>
              {t('doc_delete_patient_confirm_prefix')} <strong>{patientDetail?.name}</strong> {t('doc_delete_patient_confirm_suffix')}
              <br /><br />
              {t('doc_delete_patient_confirm_detail')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePatientDialogOpen(false)}>{t('doc_cancel')}</Button>
            <Button
              variant="destructive"
              onClick={handleDeletePatient}
              disabled={deletePatientLoading}
            >
              {deletePatientLoading ? t('doc_deleting') : t('doc_yes_delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={ordonnanceDialogOpen} onOpenChange={setOrdonnanceDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-[#10B981]" />{t('doc_create_prescription_title')}
            </DialogTitle>
            <DialogDescription>{t('doc_dialog_patient_label')} <strong>{patientDetail?.name}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">{t('doc_diagnostic_label')}</Label>
              <Input value={ordonnanceForm.diagnostic} onChange={(e) => setOrdonnanceForm((p) => ({ ...p, diagnostic: e.target.value }))} placeholder={t('doc_diagnostic_placeholder')} className="rounded-xl" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>{t('doc_medications_label')}</Label>
                <Button type="button" size="sm" variant="outline" onClick={addMedication} className="text-[#0596DE] border-[#0596DE]">
                  <Plus className="w-3 h-3 mr-1" />{t('doc_add_btn')}
                </Button>
              </div>
              <div className="space-y-3">
                {ordonnanceForm.medications.map((med, i) => (
                  <div key={i} className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[#0596DE]">{t('doc_medication_word')} {i + 1}</span>
                      {ordonnanceForm.medications.length > 1 && (
                        <button onClick={() => removeMedication(i)} className="text-red-400 hover:text-red-600">
                          <XIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative col-span-2">
                        <Input
                          value={med.name}
                          onChange={(e) => handleMedNameChange(i, e.target.value)}
                          onFocus={() => { if (med.name.trim().length >= 2) fetchMedSuggestions(med.name, i); }}
                          onBlur={() => setTimeout(() => setMedSuggestions([]), 150)}
                          placeholder={t('doc_med_name_placeholder')}
                          className="rounded-lg text-sm"
                          autoComplete="off"
                        />
                        {medSuggestLine === i && medSuggestions.length > 0 && (
                          <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-xl border border-[#E2E8F0] shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                            {medSuggestions.map((m) => (
                              <button
                                key={m.num_enregistrement || m.external_id || m.nom_commercial}
                                type="button"
                                onMouseDown={() => selectMedSuggestion(i, m)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-[#F8FAFC] border-b border-[#F1F5F9] last:border-b-0"
                              >
                                <div className="font-medium text-[#0F172A]">{m.nom_commercial}</div>
                                {(m.dosage || m.forme) && (
                                  <div className="text-xs text-[#64748B]">{[m.dosage, m.forme].filter(Boolean).join(' — ')}</div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Input value={med.dosage} onChange={(e) => handleMedDosageChange(i, e.target.value)} placeholder={t('doc_med_dosage_placeholder')} className="rounded-lg text-sm" />
                      <Input value={med.frequency} onChange={(e) => updateMedication(i, 'frequency', e.target.value)} placeholder={t('doc_med_frequency_placeholder')} className="rounded-lg text-sm" />
                      <Input value={med.duration} onChange={(e) => updateMedication(i, 'duration', e.target.value)} placeholder={t('doc_med_duration_placeholder')} className="rounded-lg text-sm col-span-2" />
                    </div>
                  </div>
                ))}
              </div>
              {(rxDuplicateGroups.length > 0 || rxSameSubstanceGroups.length > 0) && (
                <div className="space-y-2 mt-3">
                  {rxDuplicateGroups.map(([dciCode, meds]) => (
                    <div key={`rx-exact-${dciCode}`} className="bg-[#FEE2E2] border border-[#EF4444]/30 rounded-xl p-3 flex gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#EF4444] shrink-0 mt-0.5" />
                      <p className="text-xs text-[#991B1B]">
                        <strong>{meds.map((m) => m.name).join(t('drugdup_and'))}</strong>{t('doc_rx_dup_mid')}
                      </p>
                    </div>
                  ))}
                  {rxSameSubstanceGroups.map(([groupKey, meds, displayName]) => (
                    <div key={`rx-substance-${groupKey}`} className="bg-[#FFFBEB] border border-[#F59E0B]/30 rounded-xl p-3 flex gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" />
                      <p className="text-xs text-[#92400E]">
                        <strong>{meds.map((m) => `${m.name}${m.dosage ? ` (${m.dosage})` : ''}`).join(t('drugdup_and'))}</strong>
                        {t('doc_rx_samesubstance_mid')}<strong>{displayName}</strong>{t('doc_rx_samesubstance_post')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="mb-2 block">{t('doc_recommendations_label')}</Label>
              <Textarea value={ordonnanceForm.notes} onChange={(e) => setOrdonnanceForm((p) => ({ ...p, notes: e.target.value }))} placeholder={t('doc_recommendations_placeholder')} rows={3} className="rounded-xl" />
            </div>
            <div className="p-3 bg-[#D1FAE5] rounded-xl text-sm text-[#065F46]">
              {t('doc_prescription_open_notice')}
            </div>
            {/* Correctif (idee CGM) : le medecin sait a l'avance si cette
                ordonnance sera aussi versee dans l'espace du patient, ou non
                (patient hors plateforme = impression uniquement, comme avant). */}
            <div className="p-3 bg-[#F0F9FF] rounded-xl text-sm text-[#0369A1]">
              {patientDetail?.is_manual ? t('doc_prescription_not_saved_notice') : t('doc_prescription_saved_notice')}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrdonnanceDialogOpen(false)}>{t('doc_cancel')}</Button>
            <Button onClick={handlePrintOrdonnance} className="bg-[#10B981] hover:bg-[#059669] text-white">
              <Printer className="w-4 h-4 mr-2" />{t('doc_print_download_btn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✅ NOUVEAU (teleconsultation) : salle video integree (Daily.co) via
          iframe — l'URL contient un jeton nominatif a courte duree, jamais
          reutilise apres la fermeture de ce dialog. */}
      <Dialog open={videoDialogOpen} onOpenChange={(open) => { setVideoDialogOpen(open); if (!open) setVideoUrl(''); }}>
        <DialogContent className="max-w-4xl bg-white h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-[#E2E8F0]">
            <DialogTitle className="flex items-center gap-2"><Video className="w-5 h-5 text-[#0596DE]" />{t('doc_video_dialog_title')}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-black">
            {videoUrl && (
              <iframe
                src={videoUrl}
                title="Teleconsultation"
                allow="camera; microphone; fullscreen; display-capture; autoplay"
                className="w-full h-full border-0"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorDashboard;